/**
 * Femcare Web — authenticated pages.
 *
 * Every page here talks ONLY to Fertile-Track via `ft` (proxy adapter).
 * No client-side calculations for cycle/phase/predictions — those are FT-authoritative.
 * Taxonomies (symptoms/moods/flow/OPK/CM) come from `/app/frontend/src/lib/taxonomy.js`
 * which mirrors FT's canonical `/api/i18n/translations/en` — never invent options.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
    Droplet, Sparkles, Baby, Heart, Send, ArrowRight, Activity, TrendingUp, Star,
    Plus, Calendar as CalIcon, Calendar as CalendarIcon, Save, Trash2, Users, Pill, ShieldCheck,
    Flame, LineChart, Search, Zap, Handshake, ShoppingBag, Lock, Unlock, Leaf,
    ChevronLeft, ChevronRight,
} from "lucide-react";
import { ft, normalizePlansResponse } from "./api";
import { useAppAuth } from "./AuthContext";
import { useTilt } from "./PremiumStage";
import { T } from "../lib/fc3i18n";
import {
    Page, Panel, Chip, CTA, useApi, StateBlock, Field, Input, Textarea,
    Select, ChipGrid, Segmented, Modal, useToast, todayIso, DataList,
} from "./ui-kit";
import {
    SYMPTOMS, MOODS, FLOW_LEVELS, CERVICAL_MUCUS, OPK_RESULTS,
    TRACKING_STATUS, MARITAL_STATUS, CYCLE_REGULARITY,
} from "../lib/taxonomy";
import {
    getTempUnit, setTempUnit, getMassUnit, setMassUnit, displayTemp, displayMass,
} from "../lib/unit-prefs";

/* ==============================================================
   STRUCTURED SCREEN RENDERERS
   Mobile-parity screens that surface exactly the fields Femcare
   mobile exposes, with FT-canonical labels. Internal/opaque keys
   are hidden. DataList is used ONLY as a fallback when the wire
   payload has no recognised shape.
   ============================================================== */

/** Small labelled stat used across Partner / Wearables / Trends. */
function StatRow({ label, value, testid }) {
    return (
        <div className="grid grid-cols-[9rem_1fr] md:grid-cols-[12rem_1fr] gap-3 py-2.5 items-start" data-testid={testid}>
            <dt className="text-[11px] uppercase tracking-widest text-aurora-plum/60 font-bold pt-0.5">{label}</dt>
            <dd className="text-sm md:text-[15px] text-aurora-plum font-semibold break-words">{value}</dd>
        </div>
    );
}
function fmtDate(v) { if (!v) return "—"; try { return new Date(v).toLocaleString(); } catch { return String(v); } }
function fmtBool(v) { return v ? "Yes" : "No"; }
function pickNum(v, unit) { if (v === null || v === undefined) return "—"; return `${v}${unit ? " " + unit : ""}`; }

/** Partner status — the same fields the mobile Partner Sharing screen shows. */
function PartnerStatusPanel({ q }) {
    return (
        <StateBlock {...q} empty="No partner connected yet.">
            {(() => {
                const d = q.data || {};
                const connected = !!(d.connected || d.is_connected || d.linked);
                const pending   = d.pending_invite || d.invitation || d.pending;
                const partner   = d.partner || d.partner_name || d.name || null;
                const since     = d.linked_at || d.connected_at || d.created_at || null;
                return (
                    <dl className="divide-y divide-aurora-lavender/15" data-testid="partner-status">
                        <StatRow label="Partner Sharing" value={connected ? "Connected" : "Not connected"} testid="partner-connected" />
                        {connected && partner && <StatRow label="Partner" value={typeof partner === "string" ? partner : (partner.name || partner.email || "—")} />}
                        {connected && since   && <StatRow label="Connected since" value={fmtDate(since)} />}
                        {!connected && <StatRow label="Pending invite" value={pending ? (typeof pending === "string" ? pending : "Awaiting response") : "None"} testid="partner-pending" />}
                    </dl>
                );
            })()}
        </StateBlock>
    );
}

/** Wearables status — mobile shows: Premium, Devices connected, Last synced, Total entries. */
function WearablesStatusPanel({ q }) {
    return (
        <StateBlock {...q} empty="No wearables connected.">
            {(() => {
                const d = q.data || {};
                const conns = Array.isArray(d.connections) ? d.connections : (d.devices || []);
                const connCount = conns.length;
                const connLabel = connCount === 0 ? "No devices" : conns.map((c) => (typeof c === "string" ? c : (c.name || c.type || c.provider || "device"))).join(", ");
                return (
                    <dl className="divide-y divide-aurora-lavender/15" data-testid="wear-status">
                        <StatRow label="Femcare Premium" value={fmtBool(d.is_premium)} />
                        <StatRow label="Devices connected" value={connLabel} />
                        <StatRow label="Last synced" value={fmtDate(d.last_synced || d.updated_at)} />
                        <StatRow label="Total entries" value={pickNum(d.total_entries)} />
                    </dl>
                );
            })()}
        </StateBlock>
    );
}

/** Latest wearable reading — heart rate, steps, HRV, resting HR, temperature, sleep. */
function WearablesLatestPanel({ q }) {
    return (
        <StateBlock {...q} empty="No readings yet.">
            {(() => {
                const d = q.data || {};
                const shown = [
                    ["Heart rate",        d.heart_rate     ?? d.hr,      "bpm"],
                    ["Resting heart rate",d.resting_hr     ?? d.rhr,     "bpm"],
                    ["HRV",               d.hrv,                          "ms"],
                    ["Steps",             d.steps,                        ""],
                    ["Skin temperature",  d.skin_temp      ?? d.temperature ?? d.temp, "°C"],
                    ["Sleep",             d.sleep_hours    ?? d.sleep,   "h"],
                    ["Recorded at",       d.recorded_at    ?? d.timestamp ?? d.date, null],
                ].filter(([, v]) => v !== null && v !== undefined);
                if (shown.length === 0) {
                    return <p className="text-sm text-aurora-ink/55">No readings yet.</p>;
                }
                return (
                    <dl className="divide-y divide-aurora-lavender/15" data-testid="wear-latest">
                        {shown.map(([label, v, unit]) => (
                            <StatRow key={label} label={label} value={unit === null ? fmtDate(v) : `${v}${unit ? " " + unit : ""}`} />
                        ))}
                    </dl>
                );
            })()}
        </StateBlock>
    );
}

/** Health-Sync status — Total entries, Last synced, Last source. */
function HealthSyncPanel({ q }) {
    return (
        <StateBlock {...q} empty="No health-sync data yet.">
            {(() => {
                const d = q.data || {};
                return (
                    <dl className="divide-y divide-aurora-lavender/15" data-testid="wear-hs-status">
                        <StatRow label="Total entries" value={pickNum(d.total_entries)} />
                        <StatRow label="Last synced"   value={fmtDate(d.last_synced)} />
                        <StatRow label="Last source"   value={d.last_source || "—"} />
                    </dl>
                );
            })()}
        </StateBlock>
    );
}

/** Trend renderer — matches mobile Health Trends screens. */
function CyclesTrendPanel({ q }) {
    return (
        <StateBlock {...q} empty="No cycle data yet.">
            {(() => {
                const d = q.data || {};
                const items = d.cycles || d.history || [];
                return (
                    <>
                        <dl className="divide-y divide-aurora-lavender/15" data-testid="trend-cycles">
                            <StatRow label="Average cycle length"  value={pickNum(d.avg_cycle_length ?? d.average_cycle, "days")} />
                            <StatRow label="Average period length" value={pickNum(d.avg_period_length ?? d.average_period, "days")} />
                            <StatRow label="Regularity"            value={d.regularity || d.cycle_regularity || "—"} />
                            <StatRow label="Cycles tracked"        value={pickNum(d.total_cycles ?? items.length)} />
                        </dl>
                        {items.length > 0 && (
                            <ul className="mt-3 text-xs text-aurora-plum/80 space-y-1 max-h-40 overflow-auto">
                                {items.slice(-6).reverse().map((c, i) => (
                                    <li key={i} className="flex justify-between border-b border-aurora-lavender/15 py-1">
                                        <span>{c.start_date || c.start || "—"}</span>
                                        <span className="font-bold">{c.length ? `${c.length} days` : (c.cycle_length ? `${c.cycle_length} days` : "—")}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </>
                );
            })()}
        </StateBlock>
    );
}
function DistributionTrendPanel({ q, empty = "Not enough data yet." }) {
    return (
        <StateBlock {...q} empty={empty}>
            {(() => {
                const d = q.data || {};
                // Accept several shapes: {distribution:[{key,count}]}, {counts:{Cramps:5,...}}, [{mood,count}]
                let rows = [];
                if (Array.isArray(d)) rows = d;
                else if (Array.isArray(d.distribution)) rows = d.distribution;
                else if (Array.isArray(d.top))          rows = d.top;
                else if (Array.isArray(d.items))        rows = d.items;
                else if (d.counts && typeof d.counts === "object") {
                    rows = Object.entries(d.counts).map(([k, v]) => ({ label: k, count: v }));
                }
                if (rows.length === 0) return <p className="text-sm text-aurora-ink/55">{empty}</p>;
                const max = Math.max(1, ...rows.map((r) => Number(r.count || r.value || 0)));
                return (
                    <ul className="space-y-2" data-testid="trend-distribution">
                        {rows.slice(0, 10).map((r, i) => {
                            const label = r.label || r.key || r.name || r.mood || r.symptom || "Item";
                            const count = Number(r.count || r.value || 0);
                            const pct = Math.round((count / max) * 100);
                            return (
                                <li key={i}>
                                    <div className="flex justify-between text-xs mb-1">
                                        <span className="font-semibold text-aurora-plum">{label}</span>
                                        <span className="text-aurora-plum/70">{count}</span>
                                    </div>
                                    <div className="w-full h-1.5 rounded-full bg-aurora-lavender/25 overflow-hidden">
                                        <div className="h-full bg-aurora-magenta" style={{ width: `${pct}%` }} />
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                );
            })()}
        </StateBlock>
    );
}
function ReadingTrendPanel({ q, unit, testid }) {
    return (
        <StateBlock {...q} empty={`No ${unit ? unit + " " : ""}readings yet.`}>
            {(() => {
                const d = q.data || {};
                const readings = d.readings || d.series || d.entries || d.data || [];
                const values = readings.map((r) => Number(r.value || r.reading || r[unit === "°C" ? "temperature" : "weight"] || 0)).filter((n) => !Number.isNaN(n) && n > 0);
                const latest = values.length ? values[values.length - 1] : null;
                const avg    = values.length ? (values.reduce((a, b) => a + b, 0) / values.length) : null;
                const min    = values.length ? Math.min(...values) : null;
                const max    = values.length ? Math.max(...values) : null;
                return (
                    <>
                        <dl className="divide-y divide-aurora-lavender/15" data-testid={testid}>
                            <StatRow label="Latest"  value={latest !== null ? `${latest} ${unit}`.trim() : "—"} />
                            <StatRow label="Average" value={avg    !== null ? `${avg.toFixed(1)} ${unit}`.trim() : "—"} />
                            <StatRow label="Range"   value={min !== null ? `${min} – ${max} ${unit}`.trim() : "—"} />
                            <StatRow label="Readings" value={pickNum(values.length)} />
                        </dl>
                        {readings.length > 0 && (
                            <ul className="mt-3 text-xs text-aurora-plum/80 space-y-1 max-h-40 overflow-auto">
                                {readings.slice(-6).reverse().map((r, i) => (
                                    <li key={i} className="flex justify-between border-b border-aurora-lavender/15 py-1">
                                        <span>{r.date || r.recorded_at || r.timestamp || "—"}</span>
                                        <span className="font-bold">{r.value || r.reading || "—"}{unit ? " " + unit : ""}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </>
                );
            })()}
        </StateBlock>
    );
}
/**
 * Unit-aware reading trend for Temperature/Weight. FT stores metric
 * (°C, kg); we convert on-the-fly to the locale-derived user default,
 * which can be flipped by the small toggle rendered inside the panel.
 */
function UnitReadingTrendPanel({ q, kind, testid }) {
    const [unit, setUnit] = useState(kind === "temp" ? getTempUnit() : getMassUnit());
    const OPTIONS = kind === "temp" ? ["C", "F"] : ["kg", "lb"];
    const suffix  = kind === "temp" ? "°" : "";
    const flip = (u) => { setUnit(u); if (kind === "temp") setTempUnit(u); else setMassUnit(u); };
    const convert = (raw) => kind === "temp" ? displayTemp(raw, unit) : displayMass(raw, unit);
    return (
        <StateBlock {...q} empty="No readings yet.">
            {(() => {
                const d = q.data || {};
                const readings = d.readings || d.series || d.entries || d.data || [];
                const raws = readings.map((r) => Number(r.value ?? r.reading ?? r[kind === "temp" ? "temperature" : "weight"] ?? NaN)).filter((n) => !Number.isNaN(n) && n > 0);
                const latest = raws.length ? raws[raws.length - 1] : null;
                const avg    = raws.length ? (raws.reduce((a, b) => a + b, 0) / raws.length) : null;
                const min    = raws.length ? Math.min(...raws) : null;
                const max    = raws.length ? Math.max(...raws) : null;
                return (
                    <>
                        <div className="flex justify-end gap-1 mb-2">
                            {OPTIONS.map((u) => (
                                <button key={u} onClick={() => flip(u)}
                                    data-testid={`${testid}-unit-${u}`}
                                    className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold border ${
                                        unit === u
                                            ? "bg-aurora-magenta text-white border-aurora-magenta"
                                            : "bg-white text-aurora-plum border-aurora-lavender/40"
                                    }`}>{suffix}{u}</button>
                            ))}
                        </div>
                        <dl className="divide-y divide-aurora-lavender/15" data-testid={testid}>
                            <StatRow label="Latest"   value={latest !== null ? `${convert(latest).value} ${convert(latest).unit}` : "—"} />
                            <StatRow label="Average"  value={avg    !== null ? `${convert(avg).value} ${convert(avg).unit}`       : "—"} />
                            <StatRow label="Range"    value={min !== null ? `${convert(min).value} – ${convert(max).value} ${convert(min).unit}` : "—"} />
                            <StatRow label="Readings" value={pickNum(raws.length)} />
                        </dl>
                        {readings.length > 0 && (
                            <ul className="mt-3 text-xs text-aurora-plum/80 space-y-1 max-h-40 overflow-auto">
                                {readings.slice(-6).reverse().map((r, i) => {
                                    const raw = Number(r.value ?? r.reading ?? r[kind === "temp" ? "temperature" : "weight"] ?? NaN);
                                    const { value, unit: u } = Number.isNaN(raw) ? { value: "—", unit: "" } : convert(raw);
                                    return (
                                        <li key={i} className="flex justify-between border-b border-aurora-lavender/15 py-1">
                                            <span>{r.date || r.recorded_at || r.timestamp || "—"}</span>
                                            <span className="font-bold">{value} {u}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </>
                );
            })()}
        </StateBlock>
    );
}

/**
 * RichContent — renders server-generated structured content (Daughter Care
 * module/report, Community digest, Partner reads) as headings, paragraphs and
 * lists — NEVER as JSON. Supported input shapes:
 *   • plain string      → paragraph
 *   • { title, body/summary/content, sections:[{title,body,items?}], items:[…] }
 *   • { text|content|body|message }
 * Only user-facing fields are surfaced. Internal keys (`id`, `_*`, `created_at`,
 * `updated_at`, ids ending in `_id`) are hidden.
 */
function RichParagraph({ text }) {
    if (!text) return null;
    return String(text).split(/\n{2,}/).map((p, i) => (
        <p key={i} className="text-sm md:text-[15px] text-aurora-plum/85 leading-relaxed whitespace-pre-wrap mb-3 last:mb-0">{p}</p>
    ));
}
function RichSection({ node }) {
    if (!node) return null;
    if (typeof node === "string") return <RichParagraph text={node} />;
    const title = node.title || node.heading || node.name;
    const body  = node.body || node.content || node.text || node.description || node.summary;
    const items = node.items || node.tips || node.bullets;
    return (
        <section className="mb-4 last:mb-0">
            {title && <h3 className="text-base md:text-lg font-extrabold text-aurora-plum mb-1.5">{title}</h3>}
            {body && <RichParagraph text={body} />}
            {Array.isArray(items) && items.length > 0 && (
                <ul className="list-disc pl-5 mt-2 space-y-1 text-sm text-aurora-plum/85">
                    {items.map((it, i) => (
                        <li key={i}>{typeof it === "string" ? it : (it.text || it.title || JSON.stringify(it))}</li>
                    ))}
                </ul>
            )}
        </section>
    );
}
const _HIDE_KEYS = new Set(["id","_id","user_id","ft_user_id","token","access_token","created_at","updated_at","raw"]);
function RichContent({ data, emptyLabel = "Nothing here yet." , testid }) {
    if (data === null || data === undefined) {
        return <p className="text-sm text-aurora-ink/55" data-testid={testid}>{emptyLabel}</p>;
    }
    if (typeof data === "string") {
        return <div data-testid={testid}><RichParagraph text={data} /></div>;
    }
    if (Array.isArray(data)) {
        if (data.length === 0) return <p className="text-sm text-aurora-ink/55" data-testid={testid}>{emptyLabel}</p>;
        return <div data-testid={testid}>{data.map((s, i) => <RichSection key={i} node={s} />)}</div>;
    }
    if (typeof data === "object") {
        const title    = data.title || data.heading || data.name;
        const summary  = data.summary || data.subtitle;
        const body     = data.body || data.content || data.text || data.message || data.description;
        const sections = data.sections || data.chapters || data.parts;
        const items    = data.items || data.tips || data.bullets || data.highlights;
        const hasStructured = title || summary || body || Array.isArray(sections) || Array.isArray(items);
        if (hasStructured) {
            return (
                <article data-testid={testid}>
                    {title    && <h2 className="text-lg md:text-xl font-extrabold text-aurora-plum mb-1">{title}</h2>}
                    {summary  && <p className="text-sm text-aurora-plum/70 mb-3">{summary}</p>}
                    {body     && <RichParagraph text={body} />}
                    {Array.isArray(sections) && sections.map((s, i) => <RichSection key={i} node={s} />)}
                    {Array.isArray(items) && items.length > 0 && (
                        <ul className="list-disc pl-5 mt-2 space-y-1 text-sm text-aurora-plum/85">
                            {items.map((it, i) => (
                                <li key={i}>{typeof it === "string" ? it : (it.text || it.title || "")}</li>
                            ))}
                        </ul>
                    )}
                    <p className="mt-4 pt-2 border-t border-aurora-lavender/20 text-[10px] italic tracking-wide text-aurora-plum/50"
                        data-testid="rich-source">
                        Source: {data.source || data.author || data.attribution || "Femcare Health Team · Fertile-Track"}
                    </p>
                </article>
            );
        }
        // Unknown structure — render only human-facing scalar values, hide internals.
        const entries = Object.entries(data)
            .filter(([k]) => !_HIDE_KEYS.has(k) && !k.startsWith("_") && !k.endsWith("_id"))
            .filter(([, v]) => typeof v !== "object" || v === null);
        if (entries.length === 0) return <p className="text-sm text-aurora-ink/55" data-testid={testid}>{emptyLabel}</p>;
        return (
            <dl className="divide-y divide-aurora-lavender/15" data-testid={testid}>
                {entries.map(([k, v]) => (
                    <div key={k} className="grid grid-cols-[9rem_1fr] md:grid-cols-[12rem_1fr] gap-3 py-2 items-start">
                        <dt className="text-[11px] uppercase tracking-widest text-aurora-plum/60 font-bold pt-0.5">{k.replace(/[_-]+/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</dt>
                        <dd className="text-sm text-aurora-plum/90 break-words">{v === null || v === undefined || v === "" ? "—" : typeof v === "boolean" ? (v?"Yes":"No") : String(v)}</dd>
                    </div>
                ))}
            </dl>
        );
    }
    return <p className="text-sm text-aurora-plum/85" data-testid={testid}>{String(data)}</p>;
}
/* End structured screen renderers ============================== */

/* ==============================================================
   HOME — Dashboard
   ============================================================== */
/* ==============================================================
   PREMIUM ENTITLEMENT — single hook + gate component.
   A user is treated as "premium" only when FT reports an ACTIVE
   subscription. Cancelled, expired, or missing subscription →
   treated as non-premium and premium features are locked.
   ============================================================== */
function readSubscriptionState(sub) {
    const s = sub || {};
    const status = String(s.status || s.state || "").toLowerCase();
    const plan   = String(s.plan || s.tier || s.plan_id || s.subscription_type || s.type || "").toLowerCase();
    const cancelled = ["cancelled", "canceled", "expired", "past_due", "unpaid", "ended"].includes(status)
                   || s.cancelled === true || s.canceled === true;
    const active = !cancelled && (s.is_premium === true || s.premium === true || s.active === true || status === "active" || status === "trialing");
    let tier = null;
    if (plan.includes("lifetime") || plan.includes("life")) tier = "lifetime";
    else if (plan.includes("year") || plan.includes("annual")) tier = "yearly";
    else if (plan.includes("month")) tier = "monthly";
    return { isPremium: active, isCancelled: cancelled, tier, plan, status };
}
export function usePremium() {
    const q = useApi(() => ft.subscription().catch(()=>null));
    const state = useMemo(() => readSubscriptionState(q.data), [q.data]);
    return { ...state, loading: q.loading, error: q.error };
}

/**
 * PremiumLock — wraps a premium-only screen. When the user is not premium (or
 * has cancelled), renders a warm invitation instead of the screen's children.
 * Cancelled users see a "Rejoin the Femcare family" message.
 */
export function PremiumLock({ feature, children }) {
    const { isPremium, isCancelled, loading } = usePremium();
    if (loading) return <p className="text-sm text-aurora-plum/60 p-6">Checking your Femcare plan…</p>;
    if (isPremium) return children;
    return (
        <div className="max-w-3xl mx-auto" data-testid={`premium-lock-${feature.toLowerCase().replace(/ /g,"-")}`}>
            <Panel className="text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-aurora-magenta/10 border border-aurora-magenta/30 flex items-center justify-center mb-4">
                    <Lock className="w-7 h-7 text-aurora-magenta" />
                </div>
                <p className="text-[11px] uppercase tracking-widest text-aurora-magenta font-bold">Premium feature</p>
                <h2 className="mt-1 text-2xl md:text-3xl font-extrabold text-aurora-plum">
                    {isCancelled ? "Welcome back to Femcare" : `Unlock ${feature}`}
                </h2>
                <p className="mt-2 text-sm md:text-base text-aurora-plum/75 max-w-md mx-auto">
                    {isCancelled
                        ? "Your subscription ended. Renew to reunite with your Femcare family and rejoin your cycle, insights, AI coach and more."
                        : `${feature} is part of Femcare Premium. Upgrade to unlock this and every advanced feature — one plan, all devices.`}
                </p>
                <Link to="/app/subscription" data-testid="premium-upgrade-cta"
                    className="mt-5 inline-flex items-center gap-2 rounded-full bg-aurora-magenta text-white font-extrabold px-6 py-3 shadow-md hover:shadow-lg hover:bg-aurora-magenta/90 transition">
                    <Star className="w-4 h-4" /> {isCancelled ? "Rejoin Femcare Premium" : "Join Premium"}
                </Link>
                <p className="mt-4 text-xs text-aurora-plum/55">Same account across web, Android and iOS. Cancel anytime.</p>
            </Panel>
        </div>
    );
}

/* ==============================================================
   HOME DASHBOARD — Femcare mockup layout:
     · greeting header (Hi, <name> · date)
     · Femcare Shop coming-soon banner
     · Cycle rhythm hero card (circular progress ring + right column)
     · Full-month calendar with color-coded circles
     · Floating "Ask AI" pill
   All data sourced from FT via ft.cyclesCurrent / ft.calendar.
   ============================================================== */
export function AppHome() {
const { user } = useAppAuth();
const cur = useApi(() => ft.cyclesCurrent().catch(()=>null));
const sub = useApi(() => ft.subscription().catch(()=>null));
const preg = useApi(() => ft.pregnancyCurrent().catch(()=>null));
const [ym, setYm] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() + 1 }; });
const cal = useApi(() => ft.calendar(ym.y, ym.m).catch(()=>null), [ym.y, ym.m]);
const rigRef = React.useRef(null);
useTilt(rigRef, { baseX: 18, baseY: -14, ampX: 8, ampY: 14 });

const _p = preg.data || {};
const pregnancyActive = !!(_p.active === true || _p.is_active === true || _p.enabled === true
  || _p.status === "active" || (_p.week != null) || (_p.current_week != null));

const name = (user?.name || user?.email?.split("@")[0] || "there").split(" ")[0];
const today = new Date();
const hr = today.getHours();
const hello = hr < 12 ? T("Good morning") : hr < 17 ? T("Good afternoon") : T("Good evening");
const dateLabel = today.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
const c = cur.data || {};
const hasCycle = !!cur.data && !cur.error;
const cycleDay = c.day ?? c.cycle_day ?? null;
const cycleLength = c.cycle_length ?? c.length ?? 28;
const phase = c.phase || c.current_phase || null;
const daysUntil = c.days_until_next_period ?? c.days_to_next_period ?? c.next_period_days ?? null;
const nextPeriod = c.next_period || c.next_period_start || c.next_period_date || null;
const energy = c.energy || c.energy_level || (phase === "Follicular" ? "Medium" : phase === "Ovulation" ? "High" : phase === "Luteal" ? "Medium" : phase ? "Low" : "—");
const isPrem = !!(sub.data?.is_premium || sub.data?.tier === "premium" || String(sub.data?.status || "").toLowerCase() === "active");

return (
<div className="fp-home" data-testid="home-root">
  {/* ---------- Cinematic hero ---------- */}
  <section className="fp-hero" aria-labelledby="fp-hello">
    <div className="fp-aurora" aria-hidden="true"><i /><i /><i /></div>
    <div className="fp-stars" aria-hidden="true" />
    <div className="fp-grain" aria-hidden="true" />
    <div className="fp-hero-in">
      <div>
        <p className="fp-hello">{hello}</p>
        <h1 id="fp-hello"><span className="cap">{name}</span>, <em className="fp-em">{T("welcome back")}</em></h1>
        <p className="fp-date" data-testid="home-date">{dateLabel}
          {isPrem
            ? <span style={{ marginLeft: 10, padding: "3px 10px", borderRadius: 99, fontSize: 11, fontWeight: 800, background: "rgba(232,203,167,.2)", color: "#F6E7D3", border: "1px solid rgba(232,203,167,.35)" }}>★ Premium</span>
            : <span data-testid="home-plan-status" style={{ marginLeft: 10, fontSize: 12 }}>· {T("Current plan: Free")} · <Link to="/app/subscription" data-testid="home-upgrade-now" style={{ color: "#F6E7D3", fontWeight: 800, textDecoration: "underline", textUnderlineOffset: 3 }}>{T("Upgrade")}</Link></span>}
        </p>
        <div className="fp-hero-stats" data-testid="home-stats">
          <div className="fp-hstat" data-testid="home-next-period"><small><CalendarIcon aria-hidden="true" />{T("Next period")}</small><b>{daysUntil !== null ? `${daysUntil} ${T("days")}` : (nextPeriod || "—")}</b></div>
          <div className="fp-hstat" data-testid="home-today"><small><Leaf aria-hidden="true" />{T("Today")}</small><b>{cycleDay !== null ? `${T("Day")} ${cycleDay}` : "—"}</b></div>
          <div className="fp-hstat" data-testid="home-energy"><small><Zap aria-hidden="true" />{T("Energy")}</small><b>{energy}</b></div>
        </div>
        <div className="fp-hero-ctas">
          <Link to="/app/logs" className="fp-btn-champ" data-testid="home-log-today"><Plus aria-hidden="true" />{T("Log today")}</Link>
          <Link to="/app/calendar" className="fp-btn-glass" data-testid="home-view-calendar"><CalendarIcon aria-hidden="true" />{T("View calendar")}</Link>
        </div>
        {!cur.loading && !hasCycle && (
          <p style={{ marginTop: 16, fontSize: 13.5, color: "rgba(255,255,255,.75)" }}>
            {T("Log your last period to see your rhythm.")} <Link to="/app/cycle" style={{ color: "#F6E7D3", fontWeight: 800 }}>{T("Log period")} →</Link>
          </p>
        )}
      </div>
      <Ring3D rigRef={rigRef} day={cycleDay} length={cycleLength} phase={phase} daysUntil={daysUntil} loading={cur.loading} />
    </div>
  </section>

  {/* ---------- Quick actions ---------- */}
  <section className="fp-quick" aria-label={T("Quick actions")}>
    {[
      { to: "/app/cycle", label: T("Log period"), sub: T("Start or end a period"), Icon: Droplet, bg: "linear-gradient(135deg,#F0457F,#B5266A)", id: "cycle" },
      { to: "/app/logs", label: T("Daily log"), sub: T("Symptoms, mood & more"), Icon: Activity, bg: "linear-gradient(135deg,#8C7BFF,#5A49E0)", id: "logs" },
      { to: "/app/ai-coach", label: T("AI Coach"), sub: T("Ask anything, privately"), Icon: Sparkles, bg: "linear-gradient(135deg,#2A1233,#7A2A5E)", id: "ai" },
      { to: "/app/fitness", label: T("Fitness"), sub: T("Workouts for your phase"), Icon: Flame, bg: "linear-gradient(135deg,#E8A35C,#D9704A)", id: "fit" },
    ].map((q) => (
      <Link key={q.id} to={q.to} className="fp-qtile" data-testid={`home-quick-${q.id}`}>
        <span className="fp-qicon" style={{ background: q.bg }}><q.Icon aria-hidden="true" /></span>
        <span className="fp-qarrow" aria-hidden="true"><ArrowRight /></span>
        <div><b>{q.label}</b><span>{q.sub}</span></div>
      </Link>
    ))}
  </section>

  {/* ---------- Feature cards with imagery ---------- */}
  <section className="fp-feature">
    <Link to="/app/daughter-care" className="fp-fcard" data-testid="home-tile-dc">
      <div className="fp-fbg" style={{ background: "radial-gradient(120% 120% at 0% 0%, #F27BA4 0%, #D63A78 45%, #7A1E52 100%)" }} />
      <div className="fp-fimg"><img src="/femcare-ui-daughter.jpg" alt="" loading="lazy" /></div>
      <div className="fp-fcard-in">
        <div><small>Daughter Care</small><h3>{T("Support her journey")}</h3><p>{T("Age-based puberty guidance, a first-period checklist and teen-safe tracking for girls 10–17. Parents see only what's shared.")}</p></div>
        <span className="fp-fgo">{T("Open")} <ArrowRight aria-hidden="true" /></span>
      </div>
    </Link>
    {pregnancyActive ? (
      <Link to="/app/pregnancy" className="fp-fcard" data-testid="home-tile-preg">
        <div className="fp-fbg" style={{ background: "radial-gradient(120% 120% at 0% 0%, #B9AEFF 0%, #7B68EE 45%, #3B2A8C 100%)" }} />
        <div className="fp-fimg contain"><img src="/pregnancy-illustration.png" alt="" loading="lazy" /></div>
        <div className="fp-fcard-in">
          <div><small>{T("Pregnancy")}</small>
            <h3>{(_p.week ?? _p.current_week) != null ? <>{T("Week")} {_p.week ?? _p.current_week}</> : T("Your journey")}</h3>
            <p>{_p.milestone || _p.message || T("Weekly snapshots, kicks and scans")}</p></div>
          <span className="fp-fgo">{T("Open")} <ArrowRight aria-hidden="true" /></span>
        </div>
      </Link>
    ) : (
      <Link to="/app/insights" className="fp-fcard" data-testid="home-tile-insights">
        <div className="fp-fbg" style={{ background: "radial-gradient(120% 120% at 0% 0%, #B9AEFF 0%, #6B5BEF 45%, #2A1F6B 100%)" }} />
        <div className="fp-fimg"><img src="/femcare-ui-cycle.jpg" alt="" loading="lazy" /></div>
        <div className="fp-fcard-in">
          <div><small>{T("Insights")}{!isPrem && " · Premium"}</small><h3>{T("See your patterns")}</h3><p>{T("Trends and AI insights from the cycle data you choose to log.")}</p></div>
          <span className="fp-fgo">{T("Open")} <ArrowRight aria-hidden="true" /></span>
        </div>
      </Link>
    )}
  </section>

  {/* ---------- Calendar + side column ---------- */}
  <section className="fp-grid2">
    <div className="fp-panel" data-testid="home-month">
      <div className="fp-panel-head">
        <h2>{T("Your month")}</h2>
        <div className="fp-mnav">
          <button onClick={() => setYm((v) => { const p = v.m - 1; return p < 1 ? { y: v.y - 1, m: 12 } : { ...v, m: p }; })} data-testid="home-month-prev" aria-label={T("Previous month")}><ChevronLeft className="w-4 h-4" /></button>
          <span>{new Date(ym.y, ym.m - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" })}</span>
          <button onClick={() => setYm((v) => { const p = v.m + 1; return p > 12 ? { y: v.y + 1, m: 1 } : { ...v, m: p }; })} data-testid="home-month-next" aria-label={T("Next month")}><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
      <CalendarGrid data={cal.data || null} y={ym.y} m={ym.m} loading={cal.loading} />
    </div>
    <div style={{ display: "grid", gap: 18 }}>
      <div className="fp-appband" data-testid="home-app-store">
        <div>
          <h3>{T("FemCare on your phone")}</h3>
          <p>{T("Same account, daily reminders and offline logging.")}</p>
        </div>
        <div style={{ display: "flex", gap: 8 }} data-testid="home-app-store-buttons">
          <a href="https://apps.apple.com/us/app/femcare-life/id6781981749" target="_blank" rel="noreferrer noopener" aria-label="Download on the App Store" data-testid="home-app-store-ios"><img src="/app-store-badge.png" alt="Download on the App Store" /></a>
          <a href="https://play.google.com/store/apps/details?id=app.femcare.life&hl=en" target="_blank" rel="noreferrer noopener" aria-label="Get it on Google Play" data-testid="home-app-store-android"><img src="/google-play-badge.png" alt="Get it on Google Play" /></a>
        </div>
      </div>
      <div className="fp-shop" data-testid="home-shop">
        <span className="fp-qicon"><ShoppingBag aria-hidden="true" /></span>
        <div><b>Femcare Shop</b><span>{T("Coming soon · Premium exclusive — women, baby & kids essentials")}</span></div>
      </div>
      <Link to="/app/community" className="fp-shop" style={{ borderStyle: "solid", textDecoration: "none", color: "inherit" }} data-testid="home-community">
        <span className="fp-qicon" style={{ background: "linear-gradient(135deg,#E8CBA7,#C99A6E)" }}><Users aria-hidden="true" /></span>
        <div><b>{T("Community")}</b><span>{T("A private, moderated space to share and ask.")}</span></div>
      </Link>
    </div>
  </section>

  <Link to="/app/ai-coach" data-testid="home-ask-ai" className="fp-askai">
    <span className="fp-pulse" aria-hidden="true" /><Sparkles aria-hidden="true" /> {T("Ask AI")} {!isPrem && <Lock className="w-3.5 h-3.5" style={{ opacity: .8 }} />}
  </Link>
</div>
);
}

/** 3D cycle ring: layered rings at different depths + glass core, tilts with the pointer. */
function Ring3D({ rigRef, day, length, phase, daysUntil, loading }) {
const size = 300, stroke = 14, r = (size - stroke) / 2 - 6, cx = size / 2, cy = size / 2;
const circ = 2 * Math.PI * r;
const pct = length && day ? Math.min(1, Math.max(0, day / length)) : 0;
const filled = circ * pct;
const angle = (2 * Math.PI * pct) - Math.PI / 2;
const dotX = cx + r * Math.cos(angle), dotY = cy + r * Math.sin(angle);
const ticks = Array.from({ length: 56 }, (_, i) => i);
return (
<div className="fp-ring3d" data-testid="home-cycle-ring">
  <div className="fp-ring3d-rig" ref={rigRef}>
    <div className="fp-ring-disc fp-ring-l3" aria-hidden="true">
      <svg viewBox={`0 0 ${size} ${size}`}><circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(246,231,211,.5)" strokeWidth="1" strokeDasharray="2 10" /></svg>
    </div>
    <div className="fp-ring-disc fp-ring-l2" aria-hidden="true">
      <svg viewBox={`0 0 ${size} ${size}`}>
        {ticks.map((i) => { const a = (i / ticks.length) * 2 * Math.PI; const r1 = r - 2, r2 = r + (i % 7 === 0 ? 10 : 5);
          return <line key={i} x1={cx + r1 * Math.cos(a)} y1={cy + r1 * Math.sin(a)} x2={cx + r2 * Math.cos(a)} y2={cy + r2 * Math.sin(a)} stroke="rgba(255,255,255,.35)" strokeWidth={i % 7 === 0 ? 2 : 1} />; })}
      </svg>
    </div>
    <div className="fp-ring-disc fp-ring-l1">
      <svg viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <defs>
          <linearGradient id="fpRing" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#F6E7D3" /><stop offset="45%" stopColor="#FF8FB4" /><stop offset="100%" stopColor="#F0457F" /></linearGradient>
        </defs>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,.12)" strokeWidth={stroke} />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="url(#fpRing)" strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${filled} ${circ - filled}`} transform={`rotate(-90 ${cx} ${cy})`} style={{ transition: "stroke-dasharray 1.2s cubic-bezier(.2,.8,.2,1)" }} />
        {day ? <circle cx={dotX} cy={dotY} r={10} fill="#fff" stroke="#F0457F" strokeWidth="4" /> : null}
      </svg>
    </div>
    <div className="fp-ring-core">
      <small>{loading ? T("Loading") : (day ? `${T("Day")} ${day} / ${length ?? "—"}` : T("Your cycle"))}</small>
      <b data-testid="home-days-until">{daysUntil !== null && daysUntil !== undefined ? daysUntil : "—"}</b>
      <span>{T("days until your period")}</span>
      {phase && <i>{T(phase)}</i>}
    </div>
    {daysUntil !== null && daysUntil !== undefined && (
      <div className="fp-ring-tag t1" aria-hidden="true"><small>{T("Next period")}</small><b>{T("in {{n}} days", { n: daysUntil })}</b></div>
    )}
    {phase && <div className="fp-ring-tag t2" aria-hidden="true"><small>{T("Phase")}</small><b>{T(phase)}</b></div>}
  </div>
</div>
);
}

function MiniStat({ icon, tone, label, value, testid }) {
    const bg = tone === "magenta" ? "bg-aurora-magenta/10 text-aurora-magenta"
             : tone === "plum"    ? "bg-aurora-plum/10 text-aurora-plum"
             :                       "bg-aurora-lavender/20 text-aurora-lavender";
    return (
        <div className="flex items-center gap-3" data-testid={testid}>
            <div className={`w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center shrink-0 ${bg}`}>{icon}</div>
            <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-aurora-plum/55 font-bold">{label}</p>
                <p className="text-sm md:text-base font-extrabold text-aurora-plum truncate">{value}</p>
            </div>
        </div>
    );
}
/**
 * CalendarGrid — 7-column month grid matching the mockup:
 *   · Pink filled circle  = period day
 *   · Pink outline ring   = today
 *   · Purple filled       = fertile window
 *   · Green filled        = ovulation
 *   · Number only         = normal day
 * Reads FT `/api/calendar/{y}/{m}` response defensively.
 */
function CalendarGrid({ data, y, m, loading }) {
    const nav = useNavigate();
    const first = new Date(y, m - 1, 1);
    const daysInMonth = new Date(y, m, 0).getDate();
    const startWeekday = first.getDay(); // 0=Sun
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    const today = new Date();
    const isThisMonth = today.getFullYear() === y && today.getMonth() + 1 === m;

    // Extract typed dates from FT payload defensively (multiple possible shapes)
    const flags = useMemo(() => {
        const map = {}; // dateStr -> {period, fertile, ovulation, logged}
        const flag = (dateStr, k) => { if (!dateStr) return; map[dateStr] = { ...(map[dateStr] || {}), [k]: true }; };
        const d = data || {};
        (d.period_days || d.periods || []).forEach((x) => flag(typeof x === "string" ? x : (x.date || x.start), "period"));
        (d.fertile_days || d.fertile_window || []).forEach((x) => flag(typeof x === "string" ? x : x.date, "fertile"));
        (d.ovulation_days || (d.ovulation ? [d.ovulation] : [])).forEach((x) => flag(typeof x === "string" ? x : x.date, "ovulation"));
        (d.logged_days || d.logs || []).forEach((x) => flag(typeof x === "string" ? x : x.date, "logged"));
        // Some FT deployments return {days:[{date,type}]}
        (d.days || []).forEach((x) => { if (x && x.date && x.type) flag(x.date, x.type); });
        return map;
    }, [data]);
    const openLogForDate = (dateStr) => {
        // Jump to the daily-log page pre-focused on this date. The page reads ?date=YYYY-MM-DD.
        nav(`/app/logs?date=${dateStr}`);
    };
    return (
        <>
            <div className="grid grid-cols-7 text-center text-[11px] font-extrabold text-aurora-lavender uppercase tracking-wider mb-2">
                {["S","M","T","W","T","F","S"].map((d, i) => <div key={i}>{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1 md:gap-2 text-center" data-testid="home-cal-grid">
                {cells.map((n, i) => {
                    if (n === null) return <div key={i} className="h-9 md:h-10" />;
                    const dateStr = `${y}-${String(m).padStart(2,"0")}-${String(n).padStart(2,"0")}`;
                    const f = flags[dateStr] || {};
                    const isToday = isThisMonth && today.getDate() === n;
                    let cls = "text-aurora-plum/80";
                    if (f.period)         cls = "bg-aurora-magenta/25 text-aurora-magenta font-extrabold";
                    else if (f.ovulation) cls = "bg-emerald-300/60 text-emerald-800 font-extrabold";
                    else if (f.fertile)   cls = "bg-aurora-lavender/40 text-aurora-plum font-extrabold";
                    else if (f.logged)    cls = "bg-aurora-peach/40 text-aurora-plum font-extrabold";
                    if (isToday) cls += " ring-2 ring-aurora-magenta";
                    return (
                        <button key={i} type="button"
                            onClick={() => openLogForDate(dateStr)}
                            aria-label={`Log data for ${dateStr}`}
                            data-testid={`home-cal-${dateStr}`}
                            className={`h-9 md:h-10 w-9 md:w-10 mx-auto rounded-full flex items-center justify-center text-sm cursor-pointer hover:ring-2 hover:ring-aurora-magenta/60 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-aurora-magenta transition ${cls}`}>
                            {n}
                        </button>
                    );
                })}
            </div>
            {loading && <p className="mt-2 text-center text-xs text-aurora-plum/60">Loading month…</p>}
            <p className="mt-3 text-center text-[11px] text-aurora-plum/60">Tap any day to log data for that date</p>
            <div className="mt-1 flex flex-wrap gap-3 text-[11px] text-aurora-plum/70 justify-center">
                <HomeLegend swatch="bg-aurora-magenta/25" label="Period" />
                <HomeLegend swatch="bg-aurora-lavender/40" label="Fertile" />
                <HomeLegend swatch="bg-emerald-300/60" label="Ovulation" />
                <HomeLegend swatch="ring-2 ring-aurora-magenta bg-white" label="Today" />
            </div>
        </>
    );
}
function HomeLegend({ swatch, label }) {
    return <span className="inline-flex items-center gap-1.5"><span className={`w-3 h-3 rounded-full ${swatch}`}></span>{label}</span>;
}

/* ==============================================================
   CYCLE — current + Log Period + history
   ============================================================== */
export function AppCycle() {
    const cur = useApi(() => ft.cyclesCurrent().catch(()=>null));
    const hist = useApi(() => ft.cyclesHistory().catch(()=>null));
    const [openLog, setOpenLog] = useState(false);
    const toast = useToast();
    const refetch = () => { cur.__refresh?.(); hist.__refresh?.(); window.location.reload(); };

    const c = cur.data || {};
    const day = c.day ?? c.cycle_day ?? "—";
    const phase = c.phase || c.current_phase || "—";

    const history = useMemo(() => {
        const h = hist.data;
        if (Array.isArray(h)) return h;
        if (Array.isArray(h?.periods)) return h.periods;
        if (Array.isArray(h?.history)) return h.history;
        if (Array.isArray(h?.items))   return h.items;
        return [];
    }, [hist.data]);

    return (
        <Page eyebrow="Cycle" title="Your rhythm, visualised."
            subtitle="Log your period, review your history, understand your body."
            actions={<CTA testid="cycle-log-cta" onClick={()=>setOpenLog(true)}><Plus className="w-4 h-4" /> Log Period</CTA>}>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-5 mb-5">
                <Panel tone="plum" className="lg:col-span-4 flex flex-col items-center justify-center py-8">
                    <div className="relative w-44 md:w-52 h-44 md:h-52">
                        <div className="absolute inset-0 rounded-full"
                             style={{ background:"conic-gradient(#D64D8C 0deg,#EE8FAE 90deg,#B49AD9 200deg,#F7C1AC 280deg,#D64D8C 360deg)" }}/>
                        <div className="absolute inset-4 rounded-full bg-aurora-plum flex flex-col items-center justify-center">
                            <p className="text-[10px] uppercase tracking-widest text-aurora-rose/80">Day</p>
                            <p className="text-5xl md:text-6xl font-extrabold text-aurora-pearl leading-none" data-testid="cycle-day">{day}</p>
                            <p className="mt-1 text-xs text-aurora-pearl/70 italic">{phase}</p>
                        </div>
                    </div>
                    <p className="mt-4 text-xs text-aurora-pearl/70 text-center max-w-xs">
                        Predictions are calculated by Fertile-Track based on your logged data.
                    </p>
                </Panel>
                <Panel className="lg:col-span-8">
                    <p className="text-[10px] uppercase tracking-widest text-aurora-magenta font-bold mb-3 flex items-center gap-1">
                        <CalIcon className="w-3 h-3" /> Period history
                    </p>
                    <StateBlock {...hist} empty="No period logged yet. Tap 'Log Period' to add your first entry." emptyTestid="cycle-history-empty">
                        <ul className="divide-y divide-aurora-lavender/25" data-testid="cycle-history-list">
                            {history.slice(0, 30).map((h, i) => (
                                <li key={i} className="py-2.5 flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-2">
                                        <Droplet className="w-4 h-4 text-aurora-magenta" />
                                        <span className="font-semibold text-aurora-plum text-sm">{h.start_date || h.start || h.date}</span>
                                        {(h.end_date || h.end) && <span className="text-aurora-ink/60 text-xs">→ {h.end_date || h.end}</span>}
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-aurora-ink/70">
                                        {h.length && <span>{h.length} days</span>}
                                        {h.flow && <Chip>{h.flow}</Chip>}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </StateBlock>
                </Panel>
            </div>

            <LogPeriodModal open={openLog} onClose={()=>setOpenLog(false)}
                onSaved={()=>{ setOpenLog(false); toast.show("Period logged."); refetch(); }} />
            {toast.node}
        </Page>
    );
}

function LogPeriodModal({ open, onClose, onSaved }) {
    const [start, setStart] = useState(todayIso());
    const [end, setEnd]     = useState("");
    const [flow, setFlow]   = useState("medium");
    const [pain, setPain]   = useState(0);
    const [notes, setNotes] = useState("");
    const [busy, setBusy]   = useState(false);
    const [err, setErr]     = useState(null);

    const submit = async () => {
        setErr(null); setBusy(true);
        try {
            await ft.logPeriod({ start_date: start, end_date: end || null, flow, pain: Number(pain)||0, notes: notes || "" });
            onSaved?.();
        } catch (e) { setErr(e.message || "Failed to save."); }
        finally { setBusy(false); }
    };

    return <Modal open={open} onClose={onClose} title="Log Period"
        footer={<>
            <CTA variant="ghost" onClick={onClose} testid="log-period-cancel">Cancel</CTA>
            <CTA onClick={submit} disabled={busy || !start} testid="log-period-save">
                <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}
            </CTA>
        </>} testid="log-period-modal">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Start date"><Input type="date" value={start} onChange={e=>setStart(e.target.value)} testid="log-period-start" required /></Field>
            <Field label="End date (optional)"><Input type="date" value={end} onChange={e=>setEnd(e.target.value)} min={start} testid="log-period-end" /></Field>
        </div>
        <Field label="Flow level">
            <Segmented options={FLOW_LEVELS} value={flow} onChange={(v)=>setFlow(v || "medium")} testidPrefix="log-flow" />
        </Field>
        <Field label={`Pain level (${pain}/10)`}>
            <input type="range" min={0} max={10} value={pain} onChange={e=>setPain(e.target.value)}
                data-testid="log-period-pain"
                className="w-full accent-aurora-magenta mt-3" />
        </Field>
        <Field label="Notes (optional)">
            <Textarea rows={3} value={notes} onChange={e=>setNotes(e.target.value)} placeholder="How are you feeling today?" testid="log-period-notes" />
        </Field>
        {err && <p className="text-sm text-aurora-magenta" data-testid="log-period-error">{err}</p>}
    </Modal>;
}

/* ==============================================================
   CALENDAR — month grid with color-coded phases
   ============================================================== */
export function AppCalendar() {
    const [ym, setYm] = useState(() => { const d = new Date(); return { y:d.getFullYear(), m:d.getMonth()+1 }; });
    const cal = useApi(() => ft.calendar(ym.y, ym.m).catch(()=>null), [ym.y, ym.m]);
    const monthName = new Date(ym.y, ym.m-1, 1).toLocaleString(undefined, { month:"long", year:"numeric" });
    const prev = () => setYm(({y,m}) => m===1 ? { y:y-1, m:12 } : { y, m:m-1 });
    const next = () => setYm(({y,m}) => m===12 ? { y:y+1, m:1 } : { y, m:m+1 });
    return (
        <Page eyebrow="Calendar" title="Your cycle across the month"
            subtitle="Color-coded days from Fertile-Track. Predictions are backend-authoritative."
            actions={<div className="flex gap-2 items-center">
                <CTA variant="ghost" onClick={prev} testid="cal-prev">←</CTA>
                <span className="font-bold text-aurora-plum min-w-[10ch] text-center" data-testid="cal-month-label">{monthName}</span>
                <CTA variant="ghost" onClick={next} testid="cal-next">→</CTA>
            </div>}>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="cal-legend">
                <ChartLegend color="#D64D8C" label="Period" />
                <ChartLegend color="#F7C1AC" label="Predicted" />
                <ChartLegend color="#B49AD9" label="Fertile" />
                <ChartLegend color="#5FB878" label="Ovulation" />
                <ChartLegend color="#E5E7EB" label="Logged" />
            </div>
            <StateBlock {...cal} empty="No calendar data for this month.">
                <MonthGrid data={cal.data} y={ym.y} m={ym.m} />
            </StateBlock>
        </Page>
    );
}

function ChartLegend({ color, label }) {
    return <span className="inline-flex items-center gap-2 text-xs text-aurora-ink/70">
        <span className="w-3 h-3 rounded-full" style={{ background: color }} />{label}
    </span>;
}

function MonthGrid({ data, y, m }) {
    // Normalize FT calendar → { "YYYY-MM-DD": { phase, is_period, is_fertile, is_ovulation, has_log } }
    const days = useMemo(() => {
        const map = {};
        const arr = Array.isArray(data) ? data : (data?.days || data?.calendar || data?.items || []);
        for (const d of (arr || [])) {
            const dt = d.date || d.day || d.ymd;
            if (dt) map[dt] = d;
        }
        return map;
    }, [data]);

    const first = new Date(y, m-1, 1);
    const startDow = first.getDay(); // 0..6 (Sun start)
    const daysInMonth = new Date(y, m, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
        const iso = `${y}-${String(m).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
        cells.push({ d, iso, info: days[iso] || null });
    }
    const dayLabels = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
    return <div className="rounded-2xl border border-aurora-lavender/25 bg-white p-3 md:p-4">
        <div className="grid grid-cols-7 gap-1 md:gap-2 mb-2 text-[10px] font-bold text-aurora-ink/50 uppercase tracking-widest">
            {dayLabels.map(l => <div key={l} className="text-center">{l}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1 md:gap-2" data-testid="cal-grid">
            {cells.map((c, i) => {
                if (!c) return <div key={i} />;
                const info = c.info || {};
                const isPeriod = info.is_period || info.period || info.phase === "menstrual";
                const isFertile = info.is_fertile || info.fertile;
                const isOvulation = info.is_ovulation || info.ovulation;
                const isPredicted = info.is_predicted || info.predicted;
                const hasLog = info.has_log || info.log || info.logged;
                const bg = isPeriod ? "#D64D8C"
                         : isOvulation ? "#5FB878"
                         : isFertile ? "#B49AD9"
                         : isPredicted ? "#F7C1AC"
                         : hasLog ? "#E5E7EB" : "transparent";
                const fg = (isPeriod || isFertile || isOvulation) ? "#fff" : "#27182B";
                return <div key={c.iso}
                    className="aspect-square min-h-[36px] md:min-h-[52px] rounded-lg md:rounded-xl flex flex-col items-center justify-center text-xs md:text-sm font-semibold border border-aurora-lavender/20"
                    style={{ background: bg === "transparent" ? "white" : bg, color: fg }}
                    data-testid={`cal-cell-${c.iso}`}>
                    <span>{c.d}</span>
                    {hasLog && !isPeriod && <span className="text-[8px] mt-0.5">•</span>}
                </div>;
            })}
        </div>
    </div>;
}

/* ==============================================================
   LOGS — daily log editor (full FT DailyLogRequest fields)
   ============================================================== */
export function AppLogs() {
    const [searchParams] = useSearchParams();
    // Support deep-link from Home calendar (?date=YYYY-MM-DD)
    const initial = searchParams.get("date");
    const [date, setDate] = useState(initial && /^\d{4}-\d{2}-\d{2}$/.test(initial) ? initial : todayIso());
    const logQ = useApi(() => ft.getLog(date).catch(()=>null), [date]);
    const [form, setForm] = useState(() => defaultLog(date));
    const toast = useToast();
    const [busy, setBusy] = useState(false);
    const [err, setErr]   = useState(null);

    // Load existing log when date changes
    useEffect(() => { setForm(mergeLog(defaultLog(date), logQ.data)); }, [date, logQ.data]);

    const patch = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const toggleSymptom = (v) => setForm(f => {
        const arr = f.symptoms || [];
        return { ...f, symptoms: arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v] };
    });

    const save = async () => {
        setBusy(true); setErr(null);
        try {
            await ft.putLog({ ...form, date });
            toast.show("Daily log saved.");
        } catch (e) { setErr(e.message || "Failed to save."); }
        finally { setBusy(false); }
    };

    return (
        <Page eyebrow="Daily log" title="Track today, gently."
            subtitle="Same fields as the Femcare mobile app. All entries save to your canonical Femcare account."
            actions={<CTA onClick={save} disabled={busy} testid="log-save"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}</CTA>}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
                <Panel tone="white" className="lg:col-span-1">
                    <Field label="Date"><Input type="date" value={date} onChange={e=>setDate(e.target.value)} testid="log-date" /></Field>
                    <Field label="Flow"><Segmented options={FLOW_LEVELS} value={form.flow} onChange={(v)=>patch("flow", v)} testidPrefix="daily-flow" /></Field>
                    <Field label="Mood"><ChipGrid options={MOODS} values={form.mood ? [form.mood] : []} onToggle={(v)=>patch("mood", form.mood===v?null:v)} testidPrefix="daily-mood" /></Field>
                    <Field label="Cervical mucus"><Segmented options={CERVICAL_MUCUS} value={form.cervical_mucus} onChange={(v)=>patch("cervical_mucus", v)} testidPrefix="daily-cm" /></Field>
                    <Field label="Ovulation test (OPK)"><Segmented options={OPK_RESULTS} value={form.opk_result} onChange={(v)=>patch("opk_result", v)} testidPrefix="daily-opk" /></Field>
                </Panel>

                <Panel className="lg:col-span-2">
                    <Field label="Symptoms">
                        <ChipGrid options={SYMPTOMS} values={form.symptoms || []} onToggle={toggleSymptom} testidPrefix="daily-sym" cols="wide" />
                    </Field>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                        <Field label="Temperature (°C)"><Input type="number" step="0.01" value={form.temperature ?? ""} onChange={e=>patch("temperature", e.target.value?Number(e.target.value):null)} testid="daily-temp" /></Field>
                        <Field label="Weight (kg)"><Input type="number" step="0.1" value={form.weight ?? ""} onChange={e=>patch("weight", e.target.value?Number(e.target.value):null)} testid="daily-weight" /></Field>
                        <Field label="Water (glasses)"><Input type="number" min={0} value={form.water_intake ?? ""} onChange={e=>patch("water_intake", e.target.value?Number(e.target.value):null)} testid="daily-water" /></Field>
                        <Field label="Sleep (hours)"><Input type="number" step="0.1" value={form.sleep_hours ?? ""} onChange={e=>patch("sleep_hours", e.target.value?Number(e.target.value):null)} testid="daily-sleep" /></Field>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                        <Field label="Exercise">
                            <label className="mt-2 flex items-center gap-2">
                                <input type="checkbox" data-testid="daily-exercise"
                                    checked={!!form.exercise} onChange={e=>patch("exercise", e.target.checked)}
                                    className="w-5 h-5 accent-aurora-magenta" />
                                <span className="text-sm text-aurora-plum">I moved today</span>
                            </label>
                        </Field>
                        <Field label="Exercise type"><Input value={form.exercise_type ?? ""} onChange={e=>patch("exercise_type", e.target.value)} placeholder="Yoga, walking…" testid="daily-exercise-type" /></Field>
                        <Field label="Kick count (pregnancy)"><Input type="number" min={0} value={form.kick_count ?? ""} onChange={e=>patch("kick_count", e.target.value?Number(e.target.value):null)} testid="daily-kicks" /></Field>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                        <Field label="Sexual activity">
                            <label className="mt-2 flex items-center gap-2">
                                <input type="checkbox" data-testid="daily-sex"
                                    checked={!!form.sexual_activity} onChange={e=>patch("sexual_activity", e.target.checked)}
                                    className="w-5 h-5 accent-aurora-magenta" />
                                <span className="text-sm text-aurora-plum">Yes</span>
                            </label>
                        </Field>
                        <Field label="Protected?">
                            <label className="mt-2 flex items-center gap-2">
                                <input type="checkbox" data-testid="daily-protected"
                                    checked={!!form.protected} onChange={e=>patch("protected", e.target.checked)}
                                    className="w-5 h-5 accent-aurora-magenta" />
                                <span className="text-sm text-aurora-plum">Protected</span>
                            </label>
                        </Field>
                    </div>
                    <Field label="Notes"><Textarea rows={3} value={form.notes || ""} onChange={e=>patch("notes", e.target.value)} placeholder="Anything you want to remember about today…" testid="daily-notes" /></Field>
                    {err && <p className="text-sm text-aurora-magenta" data-testid="log-error">{err}</p>}
                </Panel>
            </div>

            {/* Prominent submit at the bottom so users always see how to save */}
            <div className="mt-5 flex flex-col md:flex-row items-stretch md:items-center justify-end gap-2 md:gap-3">
                {err && <p className="text-sm text-aurora-magenta md:mr-auto" data-testid="log-error-bottom">{err}</p>}
                <button type="button" onClick={save} disabled={busy}
                    data-testid="log-submit-bottom"
                    className="inline-flex items-center justify-center gap-2 rounded-full px-8 py-3 text-white font-extrabold shadow-md hover:shadow-lg hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed transition text-sm md:text-base"
                    style={{background:"linear-gradient(135deg,#EC4899 0%,#D64D8C 55%,#8F638D 100%)"}}>
                    <Save className="w-4 h-4" />
                    {busy ? "Saving…" : "Submit daily log"}
                </button>
            </div>
            {toast.node}
        </Page>
    );
}
function defaultLog(date) {
    return { date, symptoms: [], mood: null, flow: null, temperature: null, weight: null,
        water_intake: null, sleep_hours: null, exercise: false, exercise_type: null,
        sexual_activity: false, protected: false, medications: [], notes: "",
        cervical_mucus: null, opk_result: null, kick_count: null };
}
function mergeLog(base, incoming) {
    if (!incoming || typeof incoming !== "object") return base;
    const out = { ...base };
    for (const k of Object.keys(base)) if (incoming[k] !== undefined && incoming[k] !== null) out[k] = incoming[k];
    if (Array.isArray(incoming.symptoms)) out.symptoms = incoming.symptoms;
    return out;
}

/* ==============================================================
   INSIGHTS + TRENDS
   ============================================================== */
export function AppInsights() {
    const ins = useApi(() => ft.insights().catch(()=>null));
    const gen = async () => { try { await ft.generateInsights(); window.location.reload(); } catch {} };
    const items = useMemo(() => {
        const raw = ins.data;
        if (Array.isArray(raw)) return raw;
        if (Array.isArray(raw?.insights)) return raw.insights;
        if (Array.isArray(raw?.items))    return raw.items;
        return [];
    }, [ins.data]);
    return (
        <Page eyebrow="Insights" title="Patterns your body reveals."
            subtitle="Backend-generated by Fertile-Track. No fabricated metrics."
            actions={<CTA onClick={gen} variant="ghost" testid="insights-generate"><Zap className="w-4 h-4" /> Refresh</CTA>}>
            <StateBlock {...ins} empty="Insights will appear once you log a few cycles." emptyTestid="insights-empty">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="insights-grid">
                    {items.map((it, i) => (
                        <Panel key={i} tone={i%3===0?"plum":i%3===1?"lav":"peach"}>
                            <p className="text-[10px] uppercase tracking-widest font-bold mb-1"
                                style={{color: i%3===0?"#F5D5C3":"#27182B"}}>{it.category || it.tag || "Insight"}</p>
                            <p className="text-lg font-extrabold" style={{color: i%3===0?"#FFF":"#27182B"}}>{it.title || it.headline || it.name || "—"}</p>
                            <p className="mt-2 text-sm leading-relaxed" style={{color: i%3===0?"rgba(255,255,255,0.85)":"rgba(39,24,43,0.75)"}}>
                                {it.description || it.body || it.text || it.message}
                            </p>
                            <p className="mt-3 pt-2 border-t text-[10px] italic tracking-wide"
                                style={{
                                    borderColor: i%3===0 ? "rgba(255,255,255,0.15)" : "rgba(39,24,43,0.1)",
                                    color: i%3===0 ? "rgba(255,255,255,0.55)" : "rgba(39,24,43,0.5)",
                                }}
                                data-testid={`insight-source-${i}`}>
                                Source: {it.source || it.author || "Femcare Health Team · Fertile-Track"}
                            </p>
                        </Panel>
                    ))}
                </div>
            </StateBlock>
        </Page>
    );
}

export function AppTrends() {
    const cycles = useApi(() => ft.trendsCycles().catch(()=>null));
    const moods  = useApi(() => ft.trendsMood().catch(()=>null));
    const syms   = useApi(() => ft.trendsSymptoms().catch(()=>null));
    const temp   = useApi(() => ft.trendsTemperature().catch(()=>null));
    const weight = useApi(() => ft.trendsWeight().catch(()=>null));
    return (
        <Page eyebrow="Trends" title="Your patterns over time"
            subtitle="Aggregated data straight from Fertile-Track.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Panel data-testid="trend-cycles">
                    <p className="text-[10px] uppercase tracking-widest text-aurora-magenta font-bold mb-2 flex items-center gap-1">
                        <LineChart className="w-3 h-3" /> Cycle length
                    </p>
                    <CyclesTrendPanel q={cycles} />
                </Panel>
                <Panel data-testid="trend-mood">
                    <p className="text-[10px] uppercase tracking-widest text-aurora-magenta font-bold mb-2 flex items-center gap-1">
                        <LineChart className="w-3 h-3" /> Mood
                    </p>
                    <DistributionTrendPanel q={moods} empty="No mood logs yet." />
                </Panel>
                <Panel data-testid="trend-symptoms">
                    <p className="text-[10px] uppercase tracking-widest text-aurora-magenta font-bold mb-2 flex items-center gap-1">
                        <LineChart className="w-3 h-3" /> Symptoms
                    </p>
                    <DistributionTrendPanel q={syms} empty="No symptom logs yet." />
                </Panel>
                <Panel data-testid="trend-temp">
                    <p className="text-[10px] uppercase tracking-widest text-aurora-magenta font-bold mb-2 flex items-center gap-1">
                        <LineChart className="w-3 h-3" /> Temperature
                    </p>
                    <UnitReadingTrendPanel q={temp} kind="temp" testid="trend-temp-stats" />
                </Panel>
                <Panel data-testid="trend-weight">
                    <p className="text-[10px] uppercase tracking-widest text-aurora-magenta font-bold mb-2 flex items-center gap-1">
                        <LineChart className="w-3 h-3" /> Weight
                    </p>
                    <UnitReadingTrendPanel q={weight} kind="mass" testid="trend-weight-stats" />
                </Panel>
            </div>
        </Page>
    );
}

/* ==============================================================
   PROFILE — editable
   ============================================================== */
export function AppProfile() {
    const { user } = useAppAuth();
    const p = useApi(() => ft.getProfile().catch(()=>null));
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);
    const toast = useToast();
    useEffect(() => { if (p.data) setForm({
        name: p.data.name || user?.name || "",
        birth_date: p.data.birth_date || p.data.dob || "",
        cycle_length: p.data.cycle_length ?? "",
        period_length: p.data.period_length ?? "",
        height: p.data.height ?? "",
        weight: p.data.weight ?? "",
        tracking_status: p.data.tracking_status || "",
        marital_status: p.data.marital_status || "",
    }); }, [p.data, user]);

    const submit = async (e) => {
        e.preventDefault(); setSaving(true); setErr(null);
        try {
            const payload = { ...form,
                cycle_length: form.cycle_length ? Number(form.cycle_length) : null,
                period_length: form.period_length ? Number(form.period_length) : null,
                height: form.height ? Number(form.height) : null,
                weight: form.weight ? Number(form.weight) : null };
            Object.keys(payload).forEach(k => (payload[k] === "" || payload[k] === null) && delete payload[k]);
            await ft.putProfile(payload);
            toast.show("Profile updated.");
        } catch (e) { setErr(e.message || "Failed to save."); }
        finally { setSaving(false); }
    };

    return (
        <Page eyebrow="Profile" title="You"
            subtitle="Same account across Femcare mobile & web. Changes save to your canonical profile.">
            <StateBlock {...p} empty="Profile not loaded.">
                {form && (
                <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5" data-testid="profile-form">
                    <Panel tone="plum" className="lg:col-span-1">
                        <div className="flex items-center gap-3">
                            <div className="w-14 h-14 rounded-full bg-aurora-magenta flex items-center justify-center text-white text-lg font-extrabold">
                                {(user?.email||"?")[0].toUpperCase()}
                            </div>
                            <div className="min-w-0">
                                <p className="font-bold text-aurora-pearl truncate" data-testid="profile-name">{form.name || user?.email || "—"}</p>
                                <p className="text-xs text-aurora-pearl/70 truncate">{user?.email}</p>
                            </div>
                        </div>
                    </Panel>
                    <Panel className="lg:col-span-2">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Field label="Name"><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} testid="profile-input-name" maxLength={60} required /></Field>
                            <Field label="Date of Birth"><Input type="date" value={form.birth_date} onChange={e=>setForm({...form,birth_date:e.target.value})} testid="profile-input-dob" /></Field>
                            <Field label="Cycle length (days)"><Input type="number" min={20} max={45} value={form.cycle_length} onChange={e=>setForm({...form,cycle_length:e.target.value})} testid="profile-input-cycle" /></Field>
                            <Field label="Period length (days)"><Input type="number" min={2} max={10} value={form.period_length} onChange={e=>setForm({...form,period_length:e.target.value})} testid="profile-input-period" /></Field>
                            <Field label="Height (cm)"><Input type="number" step="0.1" value={form.height} onChange={e=>setForm({...form,height:e.target.value})} testid="profile-input-height" /></Field>
                            <Field label="Weight (kg)"><Input type="number" step="0.1" value={form.weight} onChange={e=>setForm({...form,weight:e.target.value})} testid="profile-input-weight" /></Field>
                            <Field label="Tracking status"><Select options={TRACKING_STATUS} value={form.tracking_status} onChange={e=>setForm({...form,tracking_status:e.target.value})} placeholder="—" testid="profile-input-tracking" /></Field>
                            <Field label="Marital status"><Select options={MARITAL_STATUS} value={form.marital_status} onChange={e=>setForm({...form,marital_status:e.target.value})} placeholder="—" testid="profile-input-marital" /></Field>
                        </div>
                        {err && <p className="text-sm text-aurora-magenta mt-2" data-testid="profile-error">{err}</p>}
                        <div className="mt-4 flex gap-2">
                            <CTA type="submit" testid="profile-save" disabled={saving}><Save className="w-4 h-4"/>{saving?"Saving…":"Save changes"}</CTA>
                        </div>
                    </Panel>
                </form>)}
            </StateBlock>
            {toast.node}
        </Page>
    );
}

/* ==============================================================
   SETTINGS — change password, notifications, delete account
   ============================================================== */
export function AppSettings() {
    const { logout } = useAppAuth();
    const nav = useNavigate();
    const notif = useApi(() => ft.notifSettings().catch(()=>null));
    const toast = useToast();
    const [pwd, setPwd] = useState({ old:"", new:"", confirm:"" });
    const [pwdBusy, setPwdBusy] = useState(false);
    const [pwdErr, setPwdErr]   = useState(null);
    const [openDelete, setOpenDelete] = useState(false);

    const changePwd = async (e) => {
        e.preventDefault(); setPwdErr(null);
        if (pwd.new.length < 6) return setPwdErr("Password must be at least 6 characters.");
        if (pwd.new !== pwd.confirm) return setPwdErr("New passwords do not match.");
        setPwdBusy(true);
        try {
            await ft.changePassword({ old_password: pwd.old, new_password: pwd.new });
            toast.show("Password updated.");
            setPwd({ old:"", new:"", confirm:"" });
        } catch (e) { setPwdErr(e.message || "Failed to change password."); }
        finally { setPwdBusy(false); }
    };

    return (
        <Page eyebrow="Settings" title="Preferences" subtitle="Manage your Femcare account.">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
                <Panel>
                    <p className="text-[10px] uppercase tracking-widest text-aurora-magenta font-bold mb-2">Change password</p>
                    <form onSubmit={changePwd} className="space-y-2" data-testid="settings-pwd-form">
                        <Field label="Current password"><Input type="password" required value={pwd.old} onChange={e=>setPwd({...pwd,old:e.target.value})} testid="pwd-old" /></Field>
                        <Field label="New password"><Input type="password" required value={pwd.new} onChange={e=>setPwd({...pwd,new:e.target.value})} testid="pwd-new" /></Field>
                        <Field label="Confirm new password"><Input type="password" required value={pwd.confirm} onChange={e=>setPwd({...pwd,confirm:e.target.value})} testid="pwd-confirm" /></Field>
                        {pwdErr && <p className="text-sm text-aurora-magenta" data-testid="pwd-error">{pwdErr}</p>}
                        <CTA type="submit" testid="pwd-save" disabled={pwdBusy}><Save className="w-4 h-4" />{pwdBusy?"Saving…":"Update password"}</CTA>
                    </form>
                </Panel>

                <Panel tone="lav">
                    <p className="text-[10px] uppercase tracking-widest text-aurora-plum font-bold mb-2">Notifications</p>
                    <StateBlock {...notif} empty="Notification preferences not available.">
                        <NotifToggles initial={notif.data} onSaved={()=>toast.show("Preferences saved.")} />
                    </StateBlock>
                </Panel>

                <Panel tone="rose" className="lg:col-span-2">
                    <p className="text-[10px] uppercase tracking-widest text-aurora-plum font-bold mb-2 flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> Account
                    </p>
                    <p className="text-sm text-aurora-plum/80 mb-3">Export your Femcare data or delete your account.</p>
                    <div className="flex gap-2 flex-wrap">
                        <CTA variant="ghost" onClick={async ()=>{
                            try {
                                // Fetch as blob so we can trigger a real browser download.
                                const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
                                const r = await fetch(`${API_BASE}/api/web/ft/api/export/cycle-data`, { credentials: "include" });
                                if (!r.ok) throw new Error(`Export failed (${r.status})`);
                                const blob = await r.blob();
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement("a"); a.href = url;
                                a.download = `femcare-cycle-data-${todayIso()}.${(r.headers.get("content-type")||"").includes("json")?"json":"csv"}`;
                                document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
                                toast.show("Export downloaded.");
                            } catch (e) { toast.show(e.message, "error"); }
                        }} testid="settings-export">Export my data</CTA>
                        <CTA variant="ghost" onClick={async ()=>{ await logout(); nav("/app/login"); }} testid="settings-logout">Sign out</CTA>
                        <CTA variant="outline" onClick={()=>setOpenDelete(true)} testid="settings-delete-open">Delete account</CTA>
                    </div>
                </Panel>
            </div>

            <Modal open={openDelete} onClose={()=>setOpenDelete(false)} title="Delete Femcare account?" testid="delete-modal"
                footer={<>
                    <CTA variant="ghost" onClick={()=>setOpenDelete(false)}>Cancel</CTA>
                    <CTA onClick={async ()=>{ try { await ft.deleteAccount(); await logout(); nav("/"); } catch(e){ toast.show(e.message,"error"); } }} testid="delete-confirm">
                        <Trash2 className="w-4 h-4" /> Delete forever
                    </CTA>
                </>}>
                <p className="text-sm text-aurora-plum/85">Your Femcare account, cycle history, logs, and every attached record will be permanently deleted from all devices (Android, iOS and web). This cannot be undone.</p>
            </Modal>
            {toast.node}
        </Page>
    );
}
function NotifToggles({ initial, onSaved }) {
    const [state, setState] = useState(() => ({
        period_reminder:   initial?.period_reminder ?? true,
        fertile_reminder:  initial?.fertile_reminder ?? true,
        ovulation_reminder:initial?.ovulation_reminder ?? true,
        daily_log_reminder:initial?.daily_log_reminder ?? true,
        weekly_digest:     initial?.weekly_digest ?? false,
    }));
    const [busy, setBusy] = useState(false);
    const save = async () => {
        setBusy(true);
        try { await ft.updateNotifSettings(state); onSaved?.(); } catch {}
        finally { setBusy(false); }
    };
    const toggle = (k) => setState(s => ({ ...s, [k]: !s[k] }));
    const rows = [
        ["period_reminder","Period reminder"],
        ["fertile_reminder","Fertile window reminder"],
        ["ovulation_reminder","Ovulation reminder"],
        ["daily_log_reminder","Daily log reminder"],
        ["weekly_digest","Weekly community digest"],
    ];
    return <div className="space-y-2">
        {rows.map(([k,label]) => (
            <label key={k} className="flex items-center justify-between gap-3 rounded-xl border border-aurora-lavender/25 bg-white/70 px-3 py-2">
                <span className="text-sm font-semibold text-aurora-plum">{label}</span>
                <input type="checkbox" checked={!!state[k]} onChange={()=>toggle(k)}
                    data-testid={`notif-${k}`} className="w-5 h-5 accent-aurora-magenta" />
            </label>
        ))}
        <CTA onClick={save} disabled={busy} testid="notif-save"><Save className="w-4 h-4" />{busy?"Saving…":"Save"}</CTA>
    </div>;
}

/* ==============================================================
   PREGNANCY — full flow
   ============================================================== */
export function AppPregnancy() {
    const cur = useApi(() => ft.pregnancyCurrent().catch(()=>null));
    const [openStart, setOpenStart] = useState(false);
    const [openEnd, setOpenEnd] = useState(false);
    const [tab, setTab] = useState("overview"); // overview | birth | contractions | scans
    const toast = useToast();
    const p = cur.data || {};
    const week = p.week ?? p.current_week ?? null;
    const active = p.active ?? (week != null);

    return (
        <Page eyebrow="Pregnancy" title="Every week, a milestone."
            subtitle="Weekly progression from your canonical Femcare pregnancy record."
            actions={active
                ? <CTA variant="outline" onClick={()=>setOpenEnd(true)} testid="preg-end-open">End pregnancy</CTA>
                : <CTA onClick={()=>setOpenStart(true)} testid="preg-start-open"><Baby className="w-4 h-4" /> Start pregnancy</CTA>}>

            <div className="flex flex-wrap gap-2 mb-4" data-testid="preg-tabs">
                {[
                    { k: "overview",     label: "Overview" },
                    { k: "birth",        label: "Birth plan" },
                    { k: "contractions", label: "Contractions" },
                    { k: "scans",        label: "Scan guide" },
                ].map(t => (
                    <button key={t.k} data-testid={`preg-tab-${t.k}`} onClick={()=>setTab(t.k)}
                        className={`rounded-full px-4 py-2 text-xs md:text-sm font-bold transition border ${
                            tab===t.k
                                ? "bg-aurora-magenta text-white border-aurora-magenta shadow"
                                : "bg-white text-aurora-plum border-aurora-lavender/30 hover:border-aurora-magenta"
                        }`}>{t.label}</button>
                ))}
            </div>

            {tab === "overview" && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                    <Panel tone="peach" className="md:col-span-2 relative overflow-hidden">
                        <div aria-hidden className="absolute -bottom-20 -right-16 w-72 h-72 rounded-full blur-3xl opacity-60"
                             style={{ background:"radial-gradient(circle,#F7C1AC 0%,transparent 70%)" }}/>
                        <p className="text-[10px] uppercase tracking-widest text-aurora-plum font-bold">Current week</p>
                        <StateBlock {...cur} empty="Enable Pregnancy Mode to start tracking your journey.">
                            <p className="mt-1 text-5xl md:text-6xl font-extrabold text-aurora-plum" data-testid="preg-week">{week ?? "—"}</p>
                            <p className="mt-1 text-sm text-aurora-plum/70">{p.milestone || p.message || "Your body is building a life."}</p>
                            {p.due_date && <p className="mt-2 text-xs text-aurora-plum/60">Due: <b>{p.due_date}</b></p>}
                            {p.trimester && <p className="mt-1 text-xs text-aurora-plum/60">Trimester: <b>{p.trimester}</b></p>}
                        </StateBlock>
                    </Panel>
                    <Panel tone="lav">
                        <p className="text-[10px] uppercase tracking-widest text-aurora-plum font-bold mb-2">Weekly guidance</p>
                        <p className="text-sm text-aurora-plum/80">{p.description || p.body || "Track kicks, symptoms and mood in Daily Log."}</p>
                    </Panel>
                </div>
            )}

            {tab === "birth"        && <BirthPlanPanel toast={toast} />}
            {tab === "contractions" && <ContractionsPanel toast={toast} />}
            {tab === "scans"        && <ScanGuidePanel />}

            <Modal open={openStart} onClose={()=>setOpenStart(false)} title="Start pregnancy" testid="preg-start-modal">
                <StartPregnancyForm onSaved={()=>{ setOpenStart(false); toast.show("Pregnancy started."); window.location.reload(); }} />
            </Modal>
            <Modal open={openEnd} onClose={()=>setOpenEnd(false)} title="End pregnancy" testid="preg-end-modal"
                footer={<CTA onClick={async ()=>{ try { await ft.pregnancyEnd({}); setOpenEnd(false); toast.show("Pregnancy ended."); window.location.reload(); } catch(e) { toast.show(e.message,"error"); } }} testid="preg-end-confirm">End</CTA>}>
                <p className="text-sm text-aurora-plum/80">This will end your current pregnancy tracking. Your logs and history are kept.</p>
            </Modal>
            {toast.node}
        </Page>
    );
}

/* Birth plan editor — full text fields backed by /api/pregnancy/birth-plan */
function BirthPlanPanel({ toast }) {
    const q = useApi(() => ft.pregnancyBirthPlan().catch(()=>null));
    const [f, set] = useState({
        preferred_delivery_type: "", pain_management: "", support_person: "",
        environment_preferences: "", labor_positions: "", after_birth_wishes: "", notes: "",
    });
    const [busy, setBusy] = useState(false);
    useEffect(() => { if (q.data && typeof q.data === "object") set(x => ({ ...x, ...q.data })); }, [q.data]);
    const save = async () => {
        setBusy(true);
        try { await ft.pregnancyBirthPlanSave(f); toast.show("Birth plan saved."); }
        catch (e) { toast.show(e.message, "error"); }
        finally { setBusy(false); }
    };
    return <Panel data-testid="birthplan-panel">
        <p className="text-[10px] uppercase tracking-widest text-aurora-magenta font-bold mb-3">Birth plan</p>
        <StateBlock {...q} loading={q.loading && !q.data} empty="Compose your birth plan below." error={null}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Preferred delivery type"><Input value={f.preferred_delivery_type||""} onChange={e=>set({...f, preferred_delivery_type:e.target.value})} testid="bp-delivery" placeholder="Vaginal / C-section / …" /></Field>
                <Field label="Pain management"><Input value={f.pain_management||""} onChange={e=>set({...f, pain_management:e.target.value})} testid="bp-pain" placeholder="Epidural / Natural / …" /></Field>
                <Field label="Support person"><Input value={f.support_person||""} onChange={e=>set({...f, support_person:e.target.value})} testid="bp-support" placeholder="Partner, doula, family" /></Field>
                <Field label="Environment preferences"><Input value={f.environment_preferences||""} onChange={e=>set({...f, environment_preferences:e.target.value})} testid="bp-env" placeholder="Dim lights, music, quiet" /></Field>
                <Field label="Labor positions"><Input value={f.labor_positions||""} onChange={e=>set({...f, labor_positions:e.target.value})} testid="bp-positions" placeholder="Squatting, birthing ball…" /></Field>
                <Field label="After-birth wishes"><Input value={f.after_birth_wishes||""} onChange={e=>set({...f, after_birth_wishes:e.target.value})} testid="bp-after" placeholder="Skin-to-skin, delayed cord clamping…" /></Field>
            </div>
            <Field label="Notes"><Textarea rows={4} value={f.notes||""} onChange={e=>set({...f, notes:e.target.value})} testid="bp-notes" /></Field>
            <CTA onClick={save} disabled={busy} testid="bp-save"><Save className="w-4 h-4" />{busy?"Saving…":"Save birth plan"}</CTA>
        </StateBlock>
    </Panel>;
}

/* Contraction timer + history — writes to /api/pregnancy/contraction-session */
function ContractionsPanel({ toast }) {
    const hist = useApi(() => ft.pregnancyContractionSessions().catch(()=>null));
    const [running, setRunning] = useState(false);
    const [contractions, setContractions] = useState([]); // [{start,end,duration}]
    const [current, setCurrent] = useState(null);        // {startAt}
    const [elapsed, setElapsed] = useState(0);
    useEffect(() => {
        if (!current) return;
        const t = setInterval(() => setElapsed(Math.floor((Date.now() - current.startAt) / 1000)), 250);
        return () => clearInterval(t);
    }, [current]);
    const toggle = () => {
        if (current) {
            const end = Date.now();
            const c = { start: new Date(current.startAt).toISOString(), end: new Date(end).toISOString(), duration_seconds: Math.floor((end - current.startAt)/1000) };
            setContractions(cs => [...cs, c]);
            setCurrent(null); setElapsed(0);
        } else {
            setCurrent({ startAt: Date.now() });
            setRunning(true);
        }
    };
    const save = async () => {
        if (!contractions.length) return toast.show("No contractions to save.", "error");
        try {
            await ft.pregnancyContractionSave({ contractions, started_at: contractions[0].start, ended_at: contractions[contractions.length-1].end });
            toast.show("Session saved.");
            setContractions([]); setRunning(false);
        } catch (e) { toast.show(e.message, "error"); }
    };
    const sessions = useMemo(() => {
        const d = hist.data; return Array.isArray(d) ? d : (d?.sessions || d?.items || []);
    }, [hist.data]);
    return <Panel data-testid="contractions-panel">
        <p className="text-[10px] uppercase tracking-widest text-aurora-magenta font-bold mb-3">Contraction timer</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1 flex flex-col items-center gap-3 rounded-2xl bg-aurora-lavender/10 border border-aurora-lavender/30 p-6">
                <p className="text-[10px] uppercase tracking-widest text-aurora-plum/70 font-bold">{current ? "Timing" : "Ready"}</p>
                <p className="text-4xl font-extrabold text-aurora-plum tabular-nums" data-testid="contraction-elapsed">
                    {String(Math.floor(elapsed/60)).padStart(2,"0")}:{String(elapsed%60).padStart(2,"0")}
                </p>
                <CTA onClick={toggle} testid="contraction-toggle" variant={current?"outline":"primary"}>
                    {current ? "Stop" : "Start contraction"}
                </CTA>
                {!!contractions.length && <CTA variant="ghost" onClick={save} testid="contraction-save"><Save className="w-4 h-4" /> Save session ({contractions.length})</CTA>}
            </div>
            <div className="md:col-span-2 rounded-2xl border border-aurora-lavender/25 bg-white p-4">
                <p className="text-xs font-bold text-aurora-plum mb-2">Current session</p>
                {contractions.length === 0 && <p className="text-sm text-aurora-ink/50">No contractions yet.</p>}
                <ul className="text-sm text-aurora-plum/85 space-y-1 max-h-56 overflow-auto" data-testid="contraction-current">
                    {contractions.map((c, i) => (
                        <li key={i} className="flex justify-between border-b border-aurora-lavender/15 py-1">
                            <span>#{i+1} · {new Date(c.start).toLocaleTimeString()}</span>
                            <span className="font-bold">{c.duration_seconds}s</span>
                        </li>
                    ))}
                </ul>
                <p className="text-xs font-bold text-aurora-plum mt-4 mb-2">Past sessions</p>
                <StateBlock {...hist} empty="No past sessions.">
                    <ul className="text-xs text-aurora-ink/70 space-y-1 max-h-40 overflow-auto" data-testid="contraction-past">
                        {sessions.slice(0, 20).map((s, i) => (
                            <li key={i} className="flex justify-between border-b border-aurora-lavender/10 py-1">
                                <span>{s.started_at || s.date || ""}</span>
                                <span>{s.count || s.contractions?.length || "—"} contractions</span>
                            </li>
                        ))}
                    </ul>
                </StateBlock>
            </div>
        </div>
    </Panel>;
}

/* Scan guide — read-only viewer over /api/pregnancy/scan-guide */
function ScanGuidePanel() {
    const q = useApi(() => ft.pregnancyScanGuide().catch(()=>null));
    const scans = useMemo(() => {
        const d = q.data; return Array.isArray(d) ? d : (d?.scans || d?.items || d?.guide || []);
    }, [q.data]);
    return <Panel data-testid="scanguide-panel">
        <p className="text-[10px] uppercase tracking-widest text-aurora-magenta font-bold mb-3">Scan guide</p>
        <StateBlock {...q} empty="No scan schedule yet.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {scans.map((s, i) => (
                    <div key={i} className="rounded-xl border border-aurora-lavender/25 bg-aurora-lavender/5 p-3">
                        <p className="text-xs uppercase text-aurora-plum/70 font-bold">{s.week ? `Week ${s.week}` : (s.range || "Scan")}</p>
                        <p className="font-bold text-aurora-plum mt-0.5">{s.title || s.name || s.scan_type || "Scan"}</p>
                        <p className="text-sm text-aurora-ink/75 mt-1">{s.description || s.body || s.details || ""}</p>
                    </div>
                ))}
            </div>
        </StateBlock>
    </Panel>;
}
function StartPregnancyForm({ onSaved }) {
    const [lmp, setLmp] = useState("");
    const [busy, setBusy] = useState(false); const [err, setErr] = useState(null);
    const submit = async () => {
        if (!lmp) return setErr("Please pick your last menstrual period date.");
        setBusy(true); setErr(null);
        try { await ft.pregnancyStart({ last_menstrual_period: lmp, lmp, start_date: lmp }); onSaved?.(); }
        catch (e) { setErr(e.message || "Failed to start pregnancy."); }
        finally { setBusy(false); }
    };
    return <>
        <Field label="Last menstrual period"><Input type="date" value={lmp} onChange={e=>setLmp(e.target.value)} testid="preg-start-lmp" /></Field>
        {err && <p className="text-sm text-aurora-magenta" data-testid="preg-start-error">{err}</p>}
        <CTA onClick={submit} disabled={busy} testid="preg-start-save"><Save className="w-4 h-4" />{busy?"Starting…":"Start"}</CTA>
    </>;
}

/* ==============================================================
   AI COACH
   ============================================================== */
export function AppAICoach() {
    const [msgs, setMsgs] = useState([{ role:"assistant", text:"Hi. I'm your Femcare AI Coach. Ask me anything about your cycle, wellness, or health." }]);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const chips = ["Am I ovulating soon?", "What foods help PMS?", "Is my cycle regular?"];
    const send = async (t) => {
        const q = (t ?? text).trim(); if (!q || busy) return;
        setMsgs(m => [...m, { role:"user", text:q }]); setText(""); setBusy(true);
        try {
            const r = await ft.aiChat(q);
            setMsgs(m => [...m, { role:"assistant", text: r?.reply || r?.message || r?.response || JSON.stringify(r) }]);
        } catch (e) { setMsgs(m => [...m, { role:"assistant", text:`Sorry — ${e.message}` }]); }
        finally { setBusy(false); }
    };
    const clear = async () => { try { await ft.aiChatClear(); } catch {} setMsgs([{role:"assistant",text:"Chat cleared. What would you like to talk about?"}]); };
    return (
        <Page eyebrow="AI Coach" title="Ask anything, gently."
            subtitle="Private, cycle-aware answers powered by Fertile-Track."
            actions={<CTA variant="outline" onClick={clear} testid="ai-clear"><Trash2 className="w-4 h-4" /> Clear</CTA>}>
            <Panel className="min-h-[560px] flex flex-col p-0 overflow-hidden">
                <div className="flex-1 overflow-auto p-4 md:p-5 space-y-3">
                    {msgs.map((m,i) => (
                        <div key={i} className={`flex ${m.role==="user"?"justify-end":"justify-start"}`}>
                            <div className={`max-w-[85%] md:max-w-[70%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
                                m.role==="user"
                                    ? "bg-aurora-magenta text-white"
                                    : "bg-aurora-lavender/15 border border-aurora-lavender/25 text-aurora-plum"
                            }`}>{m.text}</div>
                        </div>
                    ))}
                    {busy && <div className="text-xs text-aurora-plum/60 italic">Coach is thinking…</div>}
                </div>
                <div className="border-t border-aurora-lavender/30 p-3 bg-white">
                    <div className="flex flex-wrap gap-1.5 mb-2">
                        {chips.map(c => <button key={c} onClick={()=>send(c)} data-testid={`ai-chip-${c.slice(0,10)}`}
                            className="text-xs rounded-full px-2.5 py-1 bg-aurora-lavender/25 text-aurora-plum hover:bg-aurora-lavender/40">{c}</button>)}
                    </div>
                    <div className="flex gap-2">
                        <input value={text} onChange={e=>setText(e.target.value)} onKeyDown={e=>e.key==="Enter"&&send()} data-testid="ai-composer"
                            placeholder="Ask your Coach…"
                            className="flex-1 rounded-full border border-aurora-lavender/40 bg-white px-4 py-2 text-sm text-aurora-plum focus:outline-none focus:ring-2 focus:ring-aurora-magenta" />
                        <button onClick={()=>send()} disabled={busy} data-testid="ai-send"
                            className="rounded-full bg-aurora-magenta text-white px-4 py-2 disabled:opacity-60">
                            <Send className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </Panel>
        </Page>
    );
}

/* ==============================================================
   DAUGHTER CARE
   ============================================================== */
/* ==============================================================
   DAUGHTER CARE — entitlement is decided by FT's own
   /api/daughter-care/access response. That endpoint is the
   authoritative source of truth used by the mobile app. Web
   does NOT re-derive access from `subscription.plan` — it
   simply trusts and mirrors what FT tells it.

   Rules (per owner spec) that map onto the FT response:
     - `has_access: true`  → show full profile management
     - `has_access: false` + user is Premium → offer matching-tier
       Daughter Care upgrade (CTA routes to /app/subscription)
     - `has_access: false` + user is NOT Premium → upsell Premium
   ============================================================== */
function detectPremiumTier(sub) {
    const s = sub || {};
    const raw = String(s.plan || s.tier || s.plan_id || s.subscription_type || s.type || "").toLowerCase();
    const state = String(s.status || s.state || "").toLowerCase();
    const cancelled = ["cancelled","canceled","expired","past_due","unpaid","ended"].includes(state)
                   || s.cancelled === true || s.canceled === true;
    const isPremium = !cancelled && (s.is_premium || s.premium || s.active === true || state === "active" || state === "trialing" || (raw && raw !== "free" && raw !== "none"));
    let tier = null;
    if (raw.includes("lifetime") || raw.includes("life")) tier = "lifetime";
    else if (raw.includes("year") || raw.includes("annual")) tier = "yearly";
    else if (raw.includes("month")) tier = "monthly";
    return { isPremium, tier, isCancelled: cancelled };
}
function readAccess(acc) {
    const a = acc || {};
    // FT is authoritative here — accept the standard success signals
    const granted = !!(a.has_access === true || a.access === true || a.granted === true || a.access_granted === true);
    const tier = a.tier || a.plan || a.access_tier || null;
    return { granted, tier: tier ? String(tier).toLowerCase() : null };
}
const TIER_LABEL = { monthly: "Monthly", yearly: "Yearly", lifetime: "Lifetime" };

export function AppDaughterCare() {
    const nav = useNavigate();
    const sub  = useApi(() => ft.subscription().catch(()=>null));
    const acc  = useApi(() => ft.daughterAccess().catch(()=>null));
    const list = useApi(() => ft.daughterProfiles().catch(()=>null));
    const [openCreate, setOpenCreate] = useState(false);
    const toast = useToast();
    const premium = detectPremiumTier(sub.data);
    const access = readAccess(acc.data);
    const loading = sub.loading || acc.loading;
    const profiles = useMemo(() => {
        const d = list.data;
        if (Array.isArray(d)) return d;
        if (Array.isArray(d?.profiles)) return d.profiles;
        return [];
    }, [list.data]);

    return (
        <Page eyebrow="Daughter Care" title="Together, gently."
            subtitle="Puberty and wellness support for your daughter — private and respectful."
            actions={access.granted
                ? <CTA onClick={()=>setOpenCreate(true)} testid="dc-add-open"><Plus className="w-4 h-4" /> Add daughter</CTA>
                : null}>

            <DaughterCareGate loading={loading} premium={premium} access={access}
                onGoSubscription={() => nav("/app/subscription")} />

            {access.granted && (
                <Panel data-testid="dc-panel">
                    <p className="text-[10px] uppercase tracking-widest text-aurora-magenta font-bold mb-3">Daughters</p>
                    <StateBlock {...list} empty="No daughters linked yet." emptyTestid="dc-empty">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="dc-list">
                            {profiles.map((d) => <DaughterCard key={d.id||d.pid} d={d} onChanged={()=>{ window.location.reload(); }} onError={(m)=>toast.show(m,"error")} />)}
                        </div>
                    </StateBlock>
                    <p className="text-xs text-aurora-plum/70 mt-3">Generate a pair code from a daughter profile below, then enter it on her Femcare app.</p>
                </Panel>
            )}

            <Modal open={openCreate} onClose={()=>setOpenCreate(false)} title="Add daughter" testid="dc-create-modal">
                <DaughterCreateForm onSaved={()=>{ setOpenCreate(false); toast.show("Daughter profile created."); window.location.reload(); }} />
            </Modal>
            {toast.node}
        </Page>
    );
}
function DaughterCareGate({ loading, premium, access, onGoSubscription }) {
    const plansQ = useApi(() => ft.plans().catch(()=>null));
    if (loading) return <Panel tone="lav" className="mb-5"><p className="text-sm text-aurora-plum/70">Loading your Femcare Premium status…</p></Panel>;

    // Access already granted — show the same tier confirmation FT reports.
    if (access.granted) return (
        <Panel tone="lav" className="mb-5" data-testid="dc-gate-active">
            <p className="text-[10px] uppercase tracking-widest text-aurora-plum font-bold flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Daughter Care active</p>
            <p className="text-sm text-aurora-plum/85 mt-1">
                {access.tier
                    ? <>Your Daughter Care plan: <b className="text-aurora-plum">{TIER_LABEL[access.tier] || access.tier}</b></>
                    : "Your Daughter Care plan is active."}
            </p>
        </Panel>
    );

    // No access. Two states: (a) has active Premium — offer matching-tier DC.
    // (b) not premium OR cancelled — show "Upgrade to Premium to unlock" + all DC options as locked.
    const needsPremium = !premium.isPremium; // cancelled users land here too
    const headline = needsPremium
        ? "Upgrade to Premium to unlock Daughter Care plans"
        : `You're on Femcare Premium ${TIER_LABEL[premium.tier] || ""}. Add Daughter Care ${TIER_LABEL[premium.tier] || ""} to unlock daughter profiles.`;

    // Extract Daughter-Care add-on plans from the canonical FT plans response.
    const rawPlans = plansQ.data;
    const allPlans = Array.isArray(rawPlans) ? rawPlans : (rawPlans?.plans || []);
    const dcPlans = allPlans.filter(p => {
        const pid = String(p.id || p.plan_id || "").toLowerCase();
        return pid.includes("daughter");
    });
    // Fallback to a static two-tier list if FT returns none (keeps UI functional).
    const displayPlans = dcPlans.length ? dcPlans : [
        { id: "daughter_addon_monthly", name: "Daughter Care Monthly", tier: "monthly" },
        { id: "daughter_addon_yearly",  name: "Daughter Care Yearly",  tier: "yearly"  },
    ];
    const inferTier = (p) => {
        const s = String(p.id || p.plan_id || p.tier || "").toLowerCase();
        if (s.includes("year") || s.includes("annual")) return "yearly";
        if (s.includes("month")) return "monthly";
        if (s.includes("life"))  return "lifetime";
        return null;
    };
    return (
        <Panel tone="rose" className="mb-5" data-testid={needsPremium ? "dc-gate-not-premium" : "dc-gate-buy"}>
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div>
                    <p className="text-[10px] uppercase tracking-widest text-aurora-magenta font-bold flex items-center gap-1">
                        {needsPremium ? <Star className="w-3 h-3" /> : <Heart className="w-3 h-3" />}
                        {needsPremium ? "Premium required" : "Add Daughter Care"}
                    </p>
                    <p className="text-lg font-bold text-aurora-plum mt-1">{headline}</p>
                    <p className="text-sm text-aurora-plum/75 mt-1">Daughter Care follows the same billing cycle as your Premium plan.</p>
                </div>
                <CTA onClick={onGoSubscription} testid={needsPremium ? "dc-upgrade-premium" : `dc-buy-${premium.tier || "premium"}`}>
                    {needsPremium ? <><Star className="w-4 h-4" /> Upgrade to Premium</> : <><Plus className="w-4 h-4" /> Get Daughter Care</>}
                </CTA>
            </div>

            {/* Daughter Care options — show all, mark each locked/unlocked based on premium tier */}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="dc-plans-grid">
                {displayPlans.map((p, i) => {
                    const pid = p.id || p.plan_id;
                    const planTier = inferTier(p);
                    const unlocked = premium.isPremium && premium.tier && planTier && premium.tier === planTier;
                    return (
                        <button key={pid || i} type="button" onClick={onGoSubscription}
                            data-testid={`dc-plan-${planTier || i}`}
                            className={`text-left rounded-2xl border p-3 transition flex items-start gap-3 ${
                                unlocked
                                    ? "bg-white border-aurora-magenta/40 hover:border-aurora-magenta hover:shadow-md"
                                    : "bg-aurora-lavender/10 border-aurora-lavender/30 opacity-80 hover:opacity-100"
                            }`}>
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${unlocked ? "bg-aurora-magenta/15 text-aurora-magenta" : "bg-aurora-plum/10 text-aurora-plum/60"}`}>
                                {unlocked ? <Unlock className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-extrabold text-aurora-plum">{p.name || `Daughter Care ${TIER_LABEL[planTier] || ""}`}</p>
                                {p.price_inr && <p className="text-xs text-aurora-plum/70 mt-0.5">{formatMoney(p.price_inr, "INR")} / {planTier || "period"}</p>}
                                <p className="text-[11px] mt-1 font-bold" style={{color: unlocked ? "#D64D8C" : "#8677A0"}}>
                                    {unlocked
                                        ? "Available with your plan — tap to purchase"
                                        : `Requires Premium ${TIER_LABEL[planTier] || ""}`}
                                </p>
                            </div>
                        </button>
                    );
                })}
            </div>
        </Panel>
    );
}
function DaughterCard({ d, onChanged, onError }) {
    const pid = d.id || d.pid;
    const [openDetail, setOpenDetail] = useState(false);
    return <div className="p-3 rounded-xl bg-aurora-lavender/10 border border-aurora-lavender/25" data-testid={`dc-card-${pid}`}>
        <p className="font-bold text-aurora-plum">{d.name}</p>
        <p className="text-xs text-aurora-plum/70">DOB: {d.dob || "—"} · {d.relationship || "Daughter"}</p>
        <div className="mt-3 flex gap-2 flex-wrap">
            <CTA variant="ghost" testid={`dc-open-${pid}`} onClick={()=>setOpenDetail(true)}>Open</CTA>
            <CTA variant="ghost" testid={`dc-pair-${pid}`} onClick={async ()=>{ try { const r = await ft.daughterCreatePairCode(pid); alert(`Pair code: ${r?.code || r?.pair_code || JSON.stringify(r)}`); } catch(e){ onError?.(e.message); } }}>Pair code</CTA>
            <CTA variant="outline" testid={`dc-delete-${pid}`} onClick={async ()=>{ if(!confirm("Delete this profile?"))return; try { await ft.daughterDeleteProfile(pid); onChanged?.(); } catch(e){ onError?.(e.message); } }}><Trash2 className="w-3.5 h-3.5" /></CTA>
        </div>
        <Modal open={openDetail} onClose={()=>setOpenDetail(false)} title={`${d.name} — care details`} testid={`dc-detail-${pid}`}>
            <DaughterDetail pid={pid} initial={d} onChanged={onChanged} onError={onError} />
        </Modal>
    </div>;
}

/**
 * Full Daughter Care detail view — same fields the mobile app exposes:
 *   ─ Profile edit (name/dob/relationship/first-period-status/cycle-regularity/emergency contact/doctor note/privacy toggles)
 *   ─ Cycle log entry (start/end/flow/pain/notes)  → /api/daughter-care/profiles/{pid}/cycle
 *   ─ Health log entry (date/symptoms/mood/notes)  → /api/daughter-care/profiles/{pid}/health
 *   ─ Module content viewer                        → /api/daughter-care/profiles/{pid}/module
 *   ─ Report                                       → /api/daughter-care/profiles/{pid}/report
 */
function DaughterDetail({ pid, initial, onChanged, onError }) {
    const [tab, setTab] = useState("profile");
    const modQ = useApi(() => ft.daughterModule(pid).catch(()=>null), [pid]);
    const reportQ = useApi(() => ft.daughterReport(pid).catch(()=>null), [pid]);
    const [prof, setProf] = useState({
        name: initial.name || "", nickname: initial.nickname || "",
        dob: initial.dob || "", relationship: initial.relationship || "Daughter",
        first_period_status: initial.first_period_status || "unsure",
        cycle_regularity: initial.cycle_regularity || "unsure",
        emergency_contact: initial.emergency_contact || "",
        doctor_note: initial.doctor_note || "",
        privacy_share_cycle: initial.privacy_share_cycle ?? true,
        privacy_share_symptoms: initial.privacy_share_symptoms ?? true,
        privacy_share_alerts: initial.privacy_share_alerts ?? true,
        privacy_private_mode: initial.privacy_private_mode ?? false,
    });
    const [cycle, setCycle] = useState({ start_date: todayIso(), end_date: "", flow: "medium", pain: 0, notes: "" });
    const [health, setHealth] = useState({ date: todayIso(), symptoms: [], mood: "", notes: "" });
    const [busy, setBusy] = useState(false);
    const [err, setErr]   = useState(null);

    const saveProf = async () => {
        setBusy(true); setErr(null);
        try { await ft.daughterUpdateProfile(pid, prof); onChanged?.(); }
        catch (e) { setErr(e.message); } finally { setBusy(false); }
    };
    const saveCycle = async () => {
        setBusy(true); setErr(null);
        try { await ft.daughterAddCycle(pid, cycle); setCycle({ ...cycle, notes: "" }); }
        catch (e) { setErr(e.message); } finally { setBusy(false); }
    };
    const saveHealth = async () => {
        setBusy(true); setErr(null);
        try { await ft.daughterAddHealth(pid, health); setHealth({ ...health, notes: "", symptoms: [], mood: "" }); }
        catch (e) { setErr(e.message); } finally { setBusy(false); }
    };
    const toggleHealthSym = (v) => setHealth(h => ({ ...h, symptoms: h.symptoms.includes(v) ? h.symptoms.filter(x=>x!==v) : [...h.symptoms, v] }));

    return <>
        <div className="flex flex-wrap gap-2 mb-3">
            {[
                { k: "profile", label: "Profile" },
                { k: "cycle",   label: "Cycle log" },
                { k: "health",  label: "Health log" },
                { k: "module",  label: "Module" },
                { k: "report",  label: "Report" },
            ].map(t => (
                <button key={t.k} onClick={()=>setTab(t.k)} data-testid={`dc-detail-tab-${t.k}`}
                    className={`rounded-full px-3 py-1.5 text-xs font-bold border transition ${
                        tab===t.k
                            ? "bg-aurora-magenta text-white border-aurora-magenta"
                            : "bg-white text-aurora-plum border-aurora-lavender/40"
                    }`}>{t.label}</button>
            ))}
        </div>

        {tab === "profile" && <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Name"><Input value={prof.name} onChange={e=>setProf({...prof, name:e.target.value})} testid="dc-p-name" maxLength={60} /></Field>
                <Field label="Nickname"><Input value={prof.nickname} onChange={e=>setProf({...prof, nickname:e.target.value})} testid="dc-p-nick" /></Field>
                <Field label="Date of birth"><Input type="date" value={prof.dob} onChange={e=>setProf({...prof, dob:e.target.value})} testid="dc-p-dob" /></Field>
                <Field label="Relationship"><Input value={prof.relationship} onChange={e=>setProf({...prof, relationship:e.target.value})} testid="dc-p-rel" /></Field>
                <Field label="First period status">
                    <Select options={[{value:"not_started",label:"Not started"},{value:"started",label:"Started"},{value:"unsure",label:"Unsure"}]}
                        value={prof.first_period_status} onChange={e=>setProf({...prof, first_period_status:e.target.value})} testid="dc-p-first" />
                </Field>
                <Field label="Cycle regularity">
                    <Select options={CYCLE_REGULARITY} value={prof.cycle_regularity} onChange={e=>setProf({...prof, cycle_regularity:e.target.value})} testid="dc-p-reg" />
                </Field>
                <Field label="Emergency contact"><Input value={prof.emergency_contact} onChange={e=>setProf({...prof, emergency_contact:e.target.value})} testid="dc-p-em" /></Field>
                <Field label="Doctor note"><Input value={prof.doctor_note} onChange={e=>setProf({...prof, doctor_note:e.target.value})} testid="dc-p-doc" /></Field>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
                {[
                    ["privacy_share_cycle","Share cycle"],
                    ["privacy_share_symptoms","Share symptoms"],
                    ["privacy_share_alerts","Share alerts"],
                    ["privacy_private_mode","Private mode"],
                ].map(([k,label]) => (
                    <label key={k} className="flex items-center gap-2 rounded-xl border border-aurora-lavender/25 bg-white/70 px-2 py-2 text-xs">
                        <input type="checkbox" checked={!!prof[k]} onChange={()=>setProf({...prof,[k]:!prof[k]})}
                            data-testid={`dc-p-${k}`} className="w-4 h-4 accent-aurora-magenta" />
                        <span className="text-aurora-plum">{label}</span>
                    </label>
                ))}
            </div>
            {err && <p className="text-sm text-aurora-magenta mt-2" data-testid="dc-detail-error">{err}</p>}
            <div className="mt-3"><CTA onClick={saveProf} disabled={busy} testid="dc-p-save"><Save className="w-4 h-4" />{busy?"Saving…":"Save profile"}</CTA></div>
        </>}

        {tab === "cycle" && <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Start date"><Input type="date" value={cycle.start_date} onChange={e=>setCycle({...cycle, start_date:e.target.value})} testid="dc-c-start" /></Field>
                <Field label="End date"><Input type="date" value={cycle.end_date} onChange={e=>setCycle({...cycle, end_date:e.target.value})} testid="dc-c-end" /></Field>
            </div>
            <Field label="Flow"><Segmented options={FLOW_LEVELS} value={cycle.flow} onChange={(v)=>setCycle({...cycle, flow:v||"medium"})} testidPrefix="dc-c-flow" /></Field>
            <Field label={`Pain (${cycle.pain}/10)`}>
                <input type="range" min={0} max={10} value={cycle.pain} onChange={e=>setCycle({...cycle, pain:Number(e.target.value)})} data-testid="dc-c-pain" className="w-full accent-aurora-magenta mt-3" />
            </Field>
            <Field label="Notes"><Textarea rows={3} value={cycle.notes} onChange={e=>setCycle({...cycle, notes:e.target.value})} testid="dc-c-notes" /></Field>
            {err && <p className="text-sm text-aurora-magenta" data-testid="dc-c-error">{err}</p>}
            <CTA onClick={saveCycle} disabled={busy} testid="dc-c-save"><Save className="w-4 h-4" />{busy?"Saving…":"Log cycle"}</CTA>
        </>}

        {tab === "health" && <>
            <Field label="Date"><Input type="date" value={health.date} onChange={e=>setHealth({...health, date:e.target.value})} testid="dc-h-date" /></Field>
            <Field label="Symptoms"><ChipGrid options={SYMPTOMS} values={health.symptoms} onToggle={toggleHealthSym} testidPrefix="dc-h-sym" cols="wide" /></Field>
            <Field label="Mood"><ChipGrid options={MOODS} values={health.mood?[health.mood]:[]} onToggle={(v)=>setHealth({...health, mood: health.mood===v?"":v})} testidPrefix="dc-h-mood" /></Field>
            <Field label="Notes"><Textarea rows={3} value={health.notes} onChange={e=>setHealth({...health, notes:e.target.value})} testid="dc-h-notes" /></Field>
            {err && <p className="text-sm text-aurora-magenta" data-testid="dc-h-error">{err}</p>}
            <CTA onClick={saveHealth} disabled={busy} testid="dc-h-save"><Save className="w-4 h-4" />{busy?"Saving…":"Log health"}</CTA>
        </>}

        {tab === "module" && (
            <StateBlock {...modQ} empty="No module content available.">
                <div className="rounded-xl bg-aurora-lavender/10 border border-aurora-lavender/25 p-4 md:p-5 max-h-[60vh] overflow-auto">
                    <RichContent data={modQ.data} emptyLabel="No module content available." testid="dc-module-content" />
                </div>
            </StateBlock>
        )}

        {tab === "report" && (
            <StateBlock {...reportQ} empty="No report generated yet.">
                <div className="rounded-xl bg-aurora-peach/15 border border-aurora-peach/30 p-4 md:p-5 max-h-[60vh] overflow-auto">
                    <RichContent data={reportQ.data} emptyLabel="No report generated yet." testid="dc-report-content" />
                </div>
            </StateBlock>
        )}
    </>;
}
function DaughterCreateForm({ onSaved }) {
    const [f, set] = useState({ name:"", dob:"", relationship:"Daughter", first_period_status:"unsure", cycle_regularity:"unsure" });
    const [busy, setBusy] = useState(false); const [err, setErr] = useState(null);
    const submit = async () => {
        if (!f.name || !f.dob) return setErr("Name and DOB required.");
        setBusy(true); setErr(null);
        try { await ft.daughterCreateProfile(f); onSaved?.(); }
        catch (e) { setErr(e.message || "Failed to create."); }
        finally { setBusy(false); }
    };
    return <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Name"><Input value={f.name} onChange={e=>set({...f,name:e.target.value})} testid="dc-input-name" maxLength={60} required /></Field>
            <Field label="Date of birth"><Input type="date" value={f.dob} onChange={e=>set({...f,dob:e.target.value})} testid="dc-input-dob" required /></Field>
            <Field label="Relationship"><Input value={f.relationship} onChange={e=>set({...f,relationship:e.target.value})} testid="dc-input-rel" /></Field>
            <Field label="First period">
                <Select testid="dc-input-first" value={f.first_period_status} onChange={e=>set({...f,first_period_status:e.target.value})}
                    options={[{value:"not_started",label:"Not started"},{value:"started",label:"Started"},{value:"unsure",label:"Unsure"}]} />
            </Field>
            <Field label="Cycle regularity">
                <Select testid="dc-input-reg" value={f.cycle_regularity} onChange={e=>set({...f,cycle_regularity:e.target.value})} options={CYCLE_REGULARITY} />
            </Field>
        </div>
        {err && <p className="text-sm text-aurora-magenta mt-2" data-testid="dc-create-error">{err}</p>}
        <div className="mt-3"><CTA onClick={submit} disabled={busy} testid="dc-create-save"><Save className="w-4 h-4" />{busy?"Saving…":"Create"}</CTA></div>
    </>;
}

/* ==============================================================
   COMMUNITY
   ============================================================== */
export function AppCommunity() {
    const cats  = useApi(() => ft.communityCategories().catch(()=>null));
    const posts = useApi(() => ft.communityPosts().catch(()=>null));
    const groups= useApi(() => ft.communityGroups().catch(()=>null));
    const digest= useApi(() => ft.communityDigest().catch(()=>null));
    const [openCreate, setOpenCreate] = useState(false);
    const [openGroup,  setOpenGroup]  = useState(false);
    const [openDigest, setOpenDigest] = useState(false);
    const [activePost, setActivePost] = useState(null);
    const toast = useToast();
    const postList = useMemo(() => {
        const d = posts.data;
        return Array.isArray(d) ? d : (d?.posts || d?.items || []);
    }, [posts.data]);
    return (
        <Page eyebrow="Community" title="Circles, not timelines."
            subtitle="Real conversations moderated for care."
            actions={<>
                <CTA variant="ghost" onClick={()=>setOpenDigest(true)} testid="community-digest-open">Weekly digest</CTA>
                <CTA variant="ghost" onClick={()=>setOpenGroup(true)} testid="community-new-group"><Users className="w-4 h-4" /> New group</CTA>
                <CTA onClick={()=>setOpenCreate(true)} testid="community-new-post"><Plus className="w-4 h-4" /> New post</CTA>
            </>}>
            <Panel className="mb-4">
                <p className="text-[10px] uppercase tracking-widest text-aurora-magenta font-bold mb-2">Topics</p>
                <div className="flex flex-wrap gap-2" data-testid="community-topics">
                    <StateBlock {...cats}>{(cats.data||[]).map((c,i)=><Chip key={i}>{c.name||c.title||c.id}</Chip>)}</StateBlock>
                </div>
            </Panel>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
                <Panel className="lg:col-span-2">
                    <p className="text-[10px] uppercase tracking-widest text-aurora-magenta font-bold mb-3">Recent posts</p>
                    <StateBlock {...posts} empty="No posts yet. Be the first!" emptyTestid="community-empty">
                        <ul className="divide-y divide-aurora-lavender/25" data-testid="community-posts">
                            {postList.slice(0, 30).map((p) => <PostRow key={p.id} p={p} onOpen={()=>setActivePost(p)} onChanged={()=>window.location.reload()} onError={(m)=>toast.show(m,"error")} />)}
                        </ul>
                    </StateBlock>
                </Panel>
                <Panel tone="lav">
                    <p className="text-[10px] uppercase tracking-widest text-aurora-plum font-bold mb-3 flex items-center gap-1">
                        <Users className="w-3 h-3" /> Groups
                    </p>
                    <StateBlock {...groups} empty="No groups yet.">
                        <div className="grid grid-cols-2 gap-2.5" data-testid="community-groups-grid">
                            {(Array.isArray(groups.data) ? groups.data : groups.data?.groups || []).slice(0,12).map((g) => (
                                <GroupTile key={g.id} g={g} toast={toast} />
                            ))}
                        </div>
                    </StateBlock>
                </Panel>
            </div>

            <Modal open={openCreate} onClose={()=>setOpenCreate(false)} title="Share with the community" testid="community-post-modal">
                <NewPostForm onSaved={()=>{ setOpenCreate(false); toast.show("Post created."); window.location.reload(); }} />
            </Modal>
            <Modal open={openGroup} onClose={()=>setOpenGroup(false)} title="Create a group" testid="community-group-modal">
                <NewGroupForm onSaved={()=>{ setOpenGroup(false); toast.show("Group created."); window.location.reload(); }} />
            </Modal>
            <Modal open={openDigest} onClose={()=>setOpenDigest(false)} title="Weekly digest" testid="community-digest-modal">
                <StateBlock {...digest} empty="No digest yet — check back at the end of the week.">
                    <RichContent data={digest.data} emptyLabel="No digest content yet." testid="community-digest" />
                </StateBlock>
            </Modal>
            <Modal open={!!activePost} onClose={()=>setActivePost(null)} title={activePost?.title || "Post"} testid="community-post-detail">
                {activePost && <PostDetail post={activePost} onError={(m)=>toast.show(m,"error")} />}
            </Modal>
            {toast.node}
        </Page>
    );
}
function PostRow({ p, onOpen, onChanged, onError }) {
    const [busy, setBusy] = useState(false);
    return <li className="py-3" data-testid={`post-${p.id}`}>
        <div className="flex items-center justify-between gap-3">
            <button onClick={onOpen} className="text-left font-bold text-aurora-plum hover:text-aurora-magenta" data-testid={`post-open-${p.id}`}>
                {p.title || p.subject || "Post"}
            </button>
            <span className="text-[10px] text-aurora-ink/50">{p.created_at || ""}</span>
        </div>
        <p className="text-sm text-aurora-ink/75 mt-1 line-clamp-2">{p.body || p.content || p.text}</p>
        <div className="flex gap-2 mt-2 text-xs">
            <button disabled={busy} data-testid={`post-like-${p.id}`}
                onClick={async ()=>{ setBusy(true); try { await ft.communityLike(p.id); onChanged?.(); } catch(e){ onError?.(e.message); } finally { setBusy(false); } }}
                className="text-aurora-magenta font-bold">♡ {p.likes ?? p.like_count ?? 0}</button>
            <button onClick={onOpen} data-testid={`post-comments-${p.id}`} className="text-aurora-plum/60 hover:text-aurora-magenta">💬 {p.comments_count ?? p.comment_count ?? 0}</button>
        </div>
    </li>;
}
function PostDetail({ post, onError }) {
    const q = useApi(() => ft.communityGetPost(post.id).catch(()=>null), [post.id]);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!text.trim()) return;
        setBusy(true);
        try { await ft.communityComment(post.id, { body: text.trim(), content: text.trim() }); setText(""); window.location.reload(); }
        catch (e) { onError?.(e.message); }
        finally { setBusy(false); }
    };
    const detail = q.data || post;
    const comments = useMemo(() => {
        const d = detail;
        return d?.comments || d?.replies || [];
    }, [detail]);
    return <>
        <div className="rounded-xl bg-aurora-lavender/10 border border-aurora-lavender/25 p-3 mb-3">
            <p className="text-xs text-aurora-plum/60">{detail.author?.name || detail.author_name || "Anon"} · {detail.created_at || ""}</p>
            <p className="text-sm text-aurora-plum mt-1 whitespace-pre-wrap">{detail.body || detail.content || detail.text}</p>
        </div>
        <p className="text-[10px] uppercase tracking-widest text-aurora-magenta font-bold mb-2">Comments ({comments.length})</p>
        <ul className="space-y-2 max-h-64 overflow-auto pr-1" data-testid="post-comments-list">
            {comments.map(c => (
                <li key={c.id} className="rounded-xl border border-aurora-lavender/20 bg-white p-3">
                    <p className="text-xs font-semibold text-aurora-plum">{c.author?.name || c.author_name || "Anon"} <span className="text-aurora-ink/40 font-normal">· {c.created_at || ""}</span></p>
                    <p className="text-sm text-aurora-plum/85 mt-1 whitespace-pre-wrap">{c.body || c.content || c.text}</p>
                </li>
            ))}
        </ul>
        <div className="mt-3">
            <Field label="Add a comment"><Textarea rows={3} value={text} onChange={e=>setText(e.target.value)} testid="post-comment-input" /></Field>
            <CTA onClick={submit} disabled={busy || !text.trim()} testid="post-comment-submit"><Send className="w-4 h-4" />{busy?"Posting…":"Reply"}</CTA>
        </div>
    </>;
}
function NewPostForm({ onSaved }) {
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [busy, setBusy] = useState(false); const [err, setErr] = useState(null);
    const submit = async () => {
        if (!body.trim()) return setErr("Please write something.");
        setBusy(true); setErr(null);
        try { await ft.communityCreatePost({ title: title.trim() || "Post", body: body.trim(), content: body.trim() }); onSaved?.(); }
        catch (e) { setErr(e.message || "Failed to post."); }
        finally { setBusy(false); }
    };
    return <>
        <Field label="Title (optional)"><Input value={title} onChange={e=>setTitle(e.target.value)} testid="post-title" maxLength={120} /></Field>
        <Field label="Message"><Textarea rows={5} value={body} onChange={e=>setBody(e.target.value)} testid="post-body" /></Field>
        {err && <p className="text-sm text-aurora-magenta" data-testid="post-error">{err}</p>}
        <div className="mt-2"><CTA onClick={submit} disabled={busy} testid="post-submit"><Save className="w-4 h-4" />{busy?"Posting…":"Post"}</CTA></div>
    </>;
}
function NewGroupForm({ onSaved }) {
    const [name, setName] = useState("");
    const [desc, setDesc] = useState("");
    const [busy, setBusy] = useState(false); const [err, setErr] = useState(null);
    const submit = async () => {
        if (name.length < 2) return setErr("Group name must be at least 2 characters.");
        setBusy(true); setErr(null);
        try { await ft.communityCreateGroup({ name: name.trim(), description: desc.trim() || null }); onSaved?.(); }
        catch (e) { setErr(e.message || "Failed to create group."); }
        finally { setBusy(false); }
    };
    return <>
        <Field label="Group name"><Input value={name} onChange={e=>setName(e.target.value)} testid="group-name" minLength={2} maxLength={40} /></Field>
        <Field label="Description (optional)"><Textarea rows={3} value={desc} onChange={e=>setDesc(e.target.value)} testid="group-desc" maxLength={280} /></Field>
        {err && <p className="text-sm text-aurora-magenta" data-testid="group-error">{err}</p>}
        <CTA onClick={submit} disabled={busy} testid="group-submit"><Save className="w-4 h-4" />{busy?"Creating…":"Create group"}</CTA>
    </>;
}
/**
 * GroupTile — colourful thumbnail card for a community group. The gradient
 * palette + icon are picked deterministically from the group name so the same
 * group always shows the same colour scheme.
 */
const GROUP_THEMES = [
    { keys: ["mind","mood","mental"],         icon: Sparkles,   grad: "from-pink-400 to-fuchsia-500" },
    { keys: ["vaginal","health"],             icon: Heart,      grad: "from-rose-400 to-pink-500" },
    { keys: ["cycle","sync","fitness"],       icon: Activity,   grad: "from-indigo-400 to-purple-500" },
    { keys: ["ivf","journey","fertility"],    icon: Baby,       grad: "from-sky-400 to-indigo-500" },
    { keys: ["menopause"],                    icon: Star,       grad: "from-amber-400 to-orange-500" },
    { keys: ["postpartum","post-partum"],     icon: Baby,       grad: "from-teal-400 to-emerald-500" },
    { keys: ["bump","pregnan"],               icon: Baby,       grad: "from-fuchsia-400 to-purple-500" },
    { keys: ["endo","endometriosis"],         icon: Flame,      grad: "from-red-400 to-rose-500" },
    { keys: ["pcos"],                         icon: Leaf,       grad: "from-emerald-400 to-teal-500" },
    { keys: ["ttc","trying","sisters"],       icon: Heart,      grad: "from-purple-400 to-indigo-500" },
];
const GROUP_FALLBACK = [
    { icon: Users,    grad: "from-pink-400 to-purple-500" },
    { icon: Sparkles, grad: "from-fuchsia-400 to-rose-500" },
    { icon: Heart,    grad: "from-rose-400 to-fuchsia-500" },
    { icon: Star,     grad: "from-violet-400 to-purple-500" },
];
function pickGroupTheme(name) {
    const n = String(name || "").toLowerCase();
    for (const t of GROUP_THEMES) {
        if (t.keys.some(k => n.includes(k))) return t;
    }
    // Deterministic fallback by name hash so colours stay stable per group
    let h = 0; for (let i = 0; i < n.length; i++) h = (h * 31 + n.charCodeAt(i)) & 0xffff;
    return GROUP_FALLBACK[h % GROUP_FALLBACK.length];
}
function GroupTile({ g, toast }) {
    const theme = pickGroupTheme(g.name);
    const Icon = theme.icon;
    const joined = !!(g.joined || g.is_member || g.member);
    const [busy, setBusy] = useState(false);
    const doJoin = async (e) => {
        e.stopPropagation(); if (busy) return;
        setBusy(true);
        try { await ft.communityJoinGroup(g.id); toast.show("Joined."); }
        catch (err) { toast.show(err.message, "error"); }
        finally { setBusy(false); }
    };
    const doLeave = async (e) => {
        e.stopPropagation(); if (busy) return;
        setBusy(true);
        try { await ft.communityLeaveGroup(g.id); toast.show("Left."); }
        catch (err) { toast.show(err.message, "error"); }
        finally { setBusy(false); }
    };
    return (
        <div className="rounded-2xl bg-white border border-aurora-lavender/25 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition p-3 flex flex-col items-center text-center" data-testid={`community-group-${g.id}`}>
            <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${theme.grad} shadow-md flex items-center justify-center text-white`}>
                <Icon className="w-7 h-7" />
            </div>
            <p className="mt-2 text-xs md:text-sm font-extrabold text-aurora-plum leading-tight line-clamp-2">{g.name}</p>
            <div className="mt-2 flex gap-1.5">
                <button className={`text-[11px] font-bold rounded-full px-2.5 py-0.5 transition ${joined ? "bg-aurora-lavender/25 text-aurora-plum/60" : "bg-aurora-magenta text-white hover:brightness-110"}`}
                    disabled={busy || joined}
                    data-testid={`community-join-${g.id}`} onClick={doJoin}>Join</button>
                <button className="text-[11px] font-bold rounded-full px-2.5 py-0.5 bg-white text-aurora-plum/70 border border-aurora-lavender/30 hover:border-aurora-magenta transition"
                    disabled={busy}
                    data-testid={`community-leave-${g.id}`} onClick={doLeave}>Leave</button>
            </div>
        </div>
    );
}

/* ==============================================================
   MEDICATIONS
   ============================================================== */
export function AppMedications() {
    const meds = useApi(() => ft.medications().catch(()=>null));
    const [openCreate, setOpenCreate] = useState(false);
    const toast = useToast();
    const list = useMemo(() => Array.isArray(meds.data) ? meds.data : (meds.data?.medications || meds.data?.items || []), [meds.data]);
    return (
        <Page eyebrow="Medications" title="What you take, tracked"
            subtitle="Same medication list you use on the Femcare mobile app."
            actions={<CTA onClick={()=>setOpenCreate(true)} testid="med-add-open"><Plus className="w-4 h-4" /> Add</CTA>}>
            <StateBlock {...meds} empty="No medications yet." emptyTestid="med-empty">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="med-list">
                    {list.map((m) => (
                        <Panel key={m.id} className="flex items-center justify-between gap-3">
                            <div>
                                <p className="font-bold text-aurora-plum flex items-center gap-2"><Pill className="w-4 h-4 text-aurora-magenta" />{m.name}</p>
                                <p className="text-xs text-aurora-plum/70">{m.dosage || ""}{m.frequency ? ` · ${m.frequency}` : ""}</p>
                                {m.notes && <p className="text-xs text-aurora-ink/60 mt-1">{m.notes}</p>}
                            </div>
                            <div className="flex gap-2">
                                <CTA variant="ghost" testid={`med-take-${m.id}`} onClick={async ()=>{ try { await ft.takeMedication(m.id); toast.show("Marked as taken."); } catch(e){ toast.show(e.message,"error"); } }}>Take</CTA>
                                <CTA variant="outline" testid={`med-del-${m.id}`} onClick={async ()=>{ if(!confirm("Delete?"))return; try { await ft.deleteMedication(m.id); window.location.reload(); } catch(e){ toast.show(e.message,"error"); } }}><Trash2 className="w-3.5 h-3.5" /></CTA>
                            </div>
                        </Panel>
                    ))}
                </div>
            </StateBlock>
            <Modal open={openCreate} onClose={()=>setOpenCreate(false)} title="Add medication" testid="med-create-modal">
                <MedForm onSaved={()=>{ setOpenCreate(false); toast.show("Medication added."); window.location.reload(); }} />
            </Modal>
            {toast.node}
        </Page>
    );
}
function MedForm({ onSaved }) {
    const [f, set] = useState({ name:"", dosage:"", frequency:"daily", times:[], notes:"" });
    const [busy, setBusy] = useState(false); const [err, setErr] = useState(null);
    const submit = async () => {
        if (!f.name.trim()) return setErr("Name required.");
        setBusy(true); setErr(null);
        try { await ft.createMedication(f); onSaved?.(); }
        catch (e) { setErr(e.message || "Failed to save."); }
        finally { setBusy(false); }
    };
    return <>
        <Field label="Name"><Input value={f.name} onChange={e=>set({...f,name:e.target.value})} testid="med-input-name" required /></Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Dosage"><Input value={f.dosage} onChange={e=>set({...f,dosage:e.target.value})} placeholder="e.g. 200 mg" testid="med-input-dosage" /></Field>
            <Field label="Frequency"><Input value={f.frequency} onChange={e=>set({...f,frequency:e.target.value})} placeholder="daily" testid="med-input-freq" /></Field>
        </div>
        <Field label="Notes"><Textarea rows={2} value={f.notes} onChange={e=>set({...f,notes:e.target.value})} testid="med-input-notes" /></Field>
        {err && <p className="text-sm text-aurora-magenta mt-2" data-testid="med-create-error">{err}</p>}
        <CTA onClick={submit} disabled={busy} testid="med-create-save"><Save className="w-4 h-4" />{busy?"Saving…":"Add"}</CTA>
    </>;
}

/* ==============================================================
   PARTNER (P2 stub, but functional)
   ============================================================== */
export function AppPartner() {
    const status = useApi(() => ft.partnerStatus().catch(()=>null));
    const me     = useApi(() => ft.partnerMe().catch(()=>null));
    const [code, setCode] = useState("");
    const [tab, setTab] = useState("status"); // status | home | calendar | insights
    const home   = useApi(() => ft.partnerHome().catch(()=>null),   []);
    const cal    = useApi(() => ft.partnerCalendar().catch(()=>null), []);
    const insights = useApi(() => ft.partnerInsights().catch(()=>null), []);
    const toast = useToast();
    return (
        <Page eyebrow="Partner" title="Share, safely."
            subtitle="Invite your partner to view your cycle & calendar — you control everything.">
            <div className="flex flex-wrap gap-2 mb-4">
                {[
                    { k: "status",   label: "Status" },
                    { k: "home",     label: "Partner Home" },
                    { k: "calendar", label: "Partner Calendar" },
                    { k: "insights", label: "Partner Insights" },
                ].map(t => (
                    <button key={t.k} data-testid={`partner-tab-${t.k}`} onClick={()=>setTab(t.k)}
                        className={`rounded-full px-4 py-2 text-xs md:text-sm font-bold border ${
                            tab===t.k ? "bg-aurora-magenta text-white border-aurora-magenta" : "bg-white text-aurora-plum border-aurora-lavender/40"
                        }`}>{t.label}</button>
                ))}
            </div>

            {tab === "status" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Panel tone="lav">
                        <p className="text-[10px] uppercase tracking-widest text-aurora-plum font-bold mb-2">Status</p>
                        <PartnerStatusPanel q={status} />
                        <div className="mt-3">
                            <CTA variant="outline" testid="partner-remove" onClick={async ()=>{ if(!confirm("Remove partner?"))return; try { await ft.partnerRemove(); toast.show("Partner removed."); window.location.reload(); } catch(e){ toast.show(e.message,"error"); } }}>Remove partner</CTA>
                        </div>
                    </Panel>
                    <Panel>
                        <p className="text-[10px] uppercase tracking-widest text-aurora-magenta font-bold mb-2">Invite / redeem</p>
                        <CTA testid="partner-invite" onClick={async ()=>{ try { const r = await ft.partnerInvite({}); alert(`Share this code with your partner: ${r?.code || r?.token || JSON.stringify(r)}`); } catch(e){ toast.show(e.message,"error"); } }}>
                            <Handshake className="w-4 h-4" /> Generate invite code
                        </CTA>
                        <div className="mt-4">
                            <Field label="Or redeem a partner's code">
                                <div className="flex gap-2">
                                    <Input value={code} onChange={e=>setCode(e.target.value)} placeholder="ABCD-1234" testid="partner-code" />
                                    <CTA testid="partner-redeem" onClick={async ()=>{ try { await ft.partnerRedeem({ code, token: code }); toast.show("Redeemed."); window.location.reload(); } catch(e){ toast.show(e.message,"error"); } }}>Redeem</CTA>
                                </div>
                            </Field>
                        </div>
                    </Panel>
                </div>
            )}

            {tab === "home" && <PartnerReadPanel q={home} title="Partner Home" testid="partner-view-home" />}
            {tab === "calendar" && <PartnerReadPanel q={cal} title="Partner Calendar" testid="partner-view-cal" />}
            {tab === "insights" && <PartnerReadPanel q={insights} title="Partner Insights" testid="partner-view-insights" />}
            {toast.node}
        </Page>
    );
}
function PartnerReadPanel({ q, title, testid }) {
    return <Panel data-testid={testid}>
        <p className="text-[10px] uppercase tracking-widest text-aurora-magenta font-bold mb-2">{title}</p>
        <StateBlock {...q} empty="No partner data available. Ask your partner to invite you first.">
            <RichContent data={q.data} emptyLabel="No partner data yet." />
        </StateBlock>
    </Panel>;
}

/* ==============================================================
   WEARABLES / HEALTH-SYNC viewer (read-only over already-synced data)
   ============================================================== */
export function AppWearables() {
    const status = useApi(() => ft.wearablesStatus().catch(()=>null));
    const latest = useApi(() => ft.wearablesLatest().catch(()=>null));
    const hsStatus = useApi(() => ft.healthSyncStatus().catch(()=>null));
    const toast = useToast();
    return (
        <Page eyebrow="Wearables" title="Your synced signals"
            subtitle="View data your mobile device has already synced to Femcare. Mobile device sync required to add new data.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Panel tone="lav">
                    <p className="text-[10px] uppercase tracking-widest text-aurora-plum font-bold mb-2">Wearables status</p>
                    <WearablesStatusPanel q={status} />
                    <div className="mt-3 flex gap-2">
                        <CTA variant="ghost" testid="wear-sync" onClick={async ()=>{ try { await ft.wearablesSync(); toast.show("Sync requested."); } catch(e){ toast.show(e.message,"error"); } }}>Trigger sync</CTA>
                        <CTA variant="outline" testid="wear-disconnect" onClick={async ()=>{ if(!confirm("Disconnect?"))return; try { await ft.wearablesDisconnect(); toast.show("Disconnected."); window.location.reload(); } catch(e){ toast.show(e.message,"error"); } }}>Disconnect</CTA>
                    </div>
                </Panel>
                <Panel>
                    <p className="text-[10px] uppercase tracking-widest text-aurora-magenta font-bold mb-2">Latest reading</p>
                    <WearablesLatestPanel q={latest} />
                </Panel>
                <Panel tone="peach" className="md:col-span-2">
                    <p className="text-[10px] uppercase tracking-widest text-aurora-plum font-bold mb-2">Health Sync (HealthKit / Google Health Connect)</p>
                    <p className="text-xs text-aurora-plum/70 mb-3">
                        <b>Mobile device sync required</b> — HealthKit (iOS) and Health Connect (Android) collection happens on the mobile app. Web can only display data that has already been synced to your Femcare account.
                    </p>
                    <HealthSyncPanel q={hsStatus} />
                </Panel>
            </div>
            {toast.node}
        </Page>
    );
}

/* ==============================================================
   SUBSCRIPTION (unchanged from prior implementation)
   ============================================================== */
function loadRazorpay() {
    return new Promise(r => {
        if (window.Razorpay) return r(true);
        const s = document.createElement("script");
        s.src = "https://checkout.razorpay.com/v1/checkout.js";
        s.onload = () => r(true); s.onerror = () => r(false);
        document.body.appendChild(s);
    });
}
function formatMoney(minor, ccy) {
    if (minor == null) return "";
    const major = Number(minor) / 100;
    const s = major % 1 === 0 ? major.toFixed(0) : major.toFixed(2);
    return ccy === "USD" ? `$${s}` : `₹${s}`;
}
function planPeriod(p) {
    if (!p) return "";
    if (p.duration_days >= 3650) return "lifetime";
    if (p.duration_days >= 300)  return "year";
    if (p.duration_days >= 25)   return "month";
    return `${p.duration_days} days`;
}
export function AppSubscription() {
    const { user } = useAppAuth();
    const plansRes = useApi(() => ft.plans());
    const mine     = useApi(() => ft.subscription());
    const pricingCtx = useApi(() => ft.pricingContext().catch(()=>null));
    const currency = (pricingCtx.data?.currency === "INR") ? "INR" : (pricingCtx.data?.currency === "USD" ? "USD" : null);
    const [busy, setBusy] = useState(null);
    const [note, setNote] = useState(null);
    const [cancelBusy, setCancelBusy] = useState(false);
    const doCancel = async (planId) => {
        if (!window.confirm("Cancel your Femcare Premium plan? You can rejoin anytime.")) return;
        setCancelBusy(true); setNote(null);
        try {
            await ft.cancelSubscription({ plan_id: planId });
            setNote("Plan cancellation requested. Your access continues until the current period ends.");
            // Refresh subscription state so the UI flips to "Renew Now"
            setTimeout(() => window.location.reload(), 900);
        } catch (e) {
            setNote(e.message || "Could not cancel plan.");
        } finally {
            setCancelBusy(false);
        }
    };
    const { plans, features } = normalizePlansResponse(plansRes.data);
    const startCheckout = async (planId) => {
        // Early guard — refuse to call the API without a real plan id.
        // Covers undefined, null, empty string and whitespace-only values.
        if (planId === undefined || planId === null || (typeof planId === "string" && planId.trim() === "")) {
            setNote("Please select a plan first.");
            return;
        }
        setBusy(planId); setNote(null);
        try {
            const cfg = await ft.razorpayConfig().catch(()=>null);
            if (!cfg?.key_id) { setNote("Razorpay is not configured yet."); return; }
            if (!(await loadRazorpay())) { setNote("Checkout script failed to load."); return; }
            const order = await ft.razorpayCreateOrder(planId);
            const rzp = new window.Razorpay({
                key: order.rzp_key_id, order_id: order.rzp_order_id, amount: order.amount, currency: order.currency,
                name: "Femcare.life", description: "Femcare subscription",
                prefill: { email: user?.email||"", name: user?.name||"" },
                notes: { ft_intent_id: order.ft_intent_id||"" },
                handler: async (resp) => {
                    try {
                        const r = await ft.razorpayVerifyPayment({ razorpay_order_id: resp.razorpay_order_id, razorpay_payment_id: resp.razorpay_payment_id, razorpay_signature: resp.razorpay_signature, ft_intent_id: order.ft_intent_id });
                        if (r?.already_granted) setNote("Payment already applied.");
                        else if (r?.detail && /pending/i.test(r.detail)) setNote("Payment succeeded. Activating plan…");
                        else setNote("Payment verified. Welcome to FemCare Premium.");
                    } catch (e) {
                        if (e.status === 502) setNote("Payment succeeded. Activating plan…");
                        else setNote(`Verification failed: ${e.message}`);
                    }
                },
                modal: { ondismiss: ()=>setNote("Checkout dismissed.") },
                theme: { color: "#D64D8C" },
            });
            rzp.on("payment.failed", (r)=>setNote(`Payment failed (${r.error?.code||"unknown"}).`));
            rzp.open();
        } catch (e) { setNote(`Could not start checkout: ${e.message}`); }
        finally { setBusy(null); }
    };
    const currentPlanId = mine.data?.plan_id || mine.data?.plan || null;
    // Detect a cancelled/expired plan — user should be offered a "Renew Now" CTA
    // and the plan card must NOT stay disabled (that was the click-does-nothing bug).
    const _st = String(mine.data?.status || mine.data?.state || "").toLowerCase();
    const isCancelled = ["cancelled","canceled","expired","past_due","unpaid","ended"].includes(_st)
        || mine.data?.cancelled === true || mine.data?.canceled === true;
    return (
        <Page eyebrow="Subscription" title="Plans & billing"
            subtitle="Canonical plans from Fertile-Track. Web checkout via Razorpay.">
            <Panel tone="lav" className="mb-6" data-testid="sub-hero">
                <p className="text-[10px] uppercase tracking-widest text-aurora-magenta font-bold mb-2">
                    {isCancelled ? "Renew Now" : "Current plan"}
                </p>
                <StateBlock {...mine} empty="No active subscription.">
                    <p className="text-3xl md:text-4xl font-extrabold text-aurora-plum" data-testid="sub-current-plan">
                        {mine.data?.is_premium && !isCancelled ? (mine.data?.plan_name || "Premium") : "Free"}
                    </p>
                    <div className="mt-3 space-y-1.5 text-sm text-aurora-lavender">
                        <p>
                            <span className="text-aurora-plum/60">Status: </span>
                            <b className={isCancelled ? "text-aurora-magenta" : "text-aurora-lavender"}>{mine.data?.status || "—"}</b>
                        </p>
                        {mine.data?.expires_at && (
                            <p>
                                <span className="text-aurora-plum/60">{isCancelled ? "Expired on: " : "Renews: "}</span>
                                <b className="text-aurora-lavender">{new Date(mine.data.expires_at).toLocaleDateString()}</b>
                            </p>
                        )}
                        {isCancelled && (
                            <p className="text-xs text-aurora-plum/60 pt-1">Rejoin the Femcare family and unlock every premium feature again.</p>
                        )}
                    </div>
                </StateBlock>
            </Panel>
            {plansRes.loading && <p data-testid="plans-loading" className="text-sm text-aurora-ink/50 py-6 animate-pulse">Loading your plans…</p>}
            {plansRes.error && <Panel><p data-testid="plans-error" className="text-sm text-aurora-magenta">We couldn't load subscription plans. Please try again.</p></Panel>}
            {!plansRes.loading && !plansRes.error && plans.length === 0 && (
                <Panel><p data-testid="plans-empty" className="text-sm text-aurora-ink/60">No subscription plans are currently available.</p></Panel>
            )}
            {/* Checkout error / status — shown ABOVE the plans so it's the first thing the user sees */}
            {note && (
                <div className="mb-4 rounded-2xl border border-aurora-magenta/40 bg-aurora-magenta/10 px-4 py-3 flex items-start gap-3" role="alert" data-testid="checkout-note">
                    <div className="w-8 h-8 rounded-full bg-aurora-magenta/20 text-aurora-magenta flex items-center justify-center shrink-0">
                        <Lock className="w-4 h-4" />
                    </div>
                    <div className="flex-1">
                        <p className="text-[11px] uppercase tracking-widest text-aurora-magenta font-bold">Checkout note</p>
                        <p className="text-sm text-aurora-plum mt-0.5 whitespace-pre-wrap">{note}</p>
                    </div>
                    <button aria-label="Dismiss" data-testid="checkout-note-dismiss"
                        onClick={() => setNote(null)}
                        className="text-aurora-plum/50 hover:text-aurora-magenta text-lg leading-none">×</button>
                </div>
            )}
            {!plansRes.loading && plans.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="plans-grid">
                    {plans.map((p, i) => {
                        const pid = p.id || p.plan_id;
                        const isCurrent = currentPlanId && (currentPlanId === pid);
                        const featuresList = p.features || features?.[p.tier] || [];
                        const period = planPeriod(p);
                        return (
                            <Panel key={pid || i} tone={isCurrent ? "peach" : (p.badge ? "lav" : "pearl")} className="flex flex-col" data-testid={`plan-card-${pid}`}>
                                <div className="flex items-center justify-between">
                                    <p className="text-[10px] uppercase tracking-widest text-aurora-plum font-bold">{p.tier || "Plan"}</p>
                                    {p.badge && <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-aurora-magenta text-white">{p.badge}</span>}
                                </div>
                                <h3 className="text-2xl font-extrabold text-aurora-plum mt-1">{p.name || pid}</h3>
                                <p className="text-3xl font-extrabold mt-3 text-aurora-magenta" data-testid={`plan-price-${pid}`}>
                                    {currency === "USD"
                                        ? formatMoney(p.price_usd, "USD")
                                        : formatMoney(p.price_inr, "INR")}
                                    <span className="text-sm text-aurora-ink/60"> / {period}</span>
                                </p>
                                {p.savings_pct > 0 && <p className="text-xs text-aurora-plum/70 mt-1">Save {p.savings_pct}%</p>}
                                <ul className="mt-3 space-y-1 text-sm text-aurora-ink/80 flex-1">
                                    {featuresList.slice(0, 6).map((f, k) => <li key={k}>· {typeof f === "string" ? f : (f?.name || f?.title || "Feature")}</li>)}
                                </ul>
                                <button onClick={() => startCheckout(pid)} disabled={busy === pid || (isCurrent && !isCancelled) || !pid}
                                    className="mt-5 rounded-full px-5 py-2.5 text-white font-bold text-sm disabled:opacity-50"
                                    style={{ background:"linear-gradient(135deg,#D64D8C 0%,#27182B 100%)" }}
                                    data-testid={`plan-select-${pid || i}`}>
                                    {isCurrent && !isCancelled
                                        ? "Current plan"
                                        : busy === pid
                                            ? "Preparing…"
                                            : (isCurrent && isCancelled ? "Renew Now" : "Subscribe")}
                                </button>
                                {isCurrent && !isCancelled && (
                                    <button
                                        onClick={() => doCancel(pid)}
                                        disabled={cancelBusy}
                                        data-testid={`plan-cancel-${pid}`}
                                        className="mt-2 text-xs font-bold text-aurora-plum/60 hover:text-aurora-magenta underline underline-offset-2 disabled:opacity-50 disabled:no-underline transition self-start"
                                    >
                                        {cancelBusy ? "Cancelling…" : "Cancel plan"}
                                    </button>
                                )}
                            </Panel>
                        );
                    })}
                </div>
            )}
        </Page>
    );
}
